import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Loader2, Sparkles, CheckCircle, Wand2, X, Layers, Pencil, AlertTriangle, Info, Lightbulb, ChevronDown, ChevronUp, Link2, Plus, GraduationCap, Tag, Briefcase, Zap, BookOpen, Languages } from 'lucide-react';
import { getUser, getAuthToken, authenticatedFetch } from '@/lib/auth';
import { useToast, useConfirm } from './ui';
import { validateECTSAlignment, validateLearningOutcomes } from '@/lib/validation';
import type { StudyPlanFormData, ValidationResponse, ValidationFeedback, StudyPlanModule, OutcomeESCOMapping, ESCOOccupationMatch, ESCOSkillMatch } from '@/types';
import LiteratureManager from './LiteratureManager';
import ModuleEditor from './ModuleEditor';
import LearningOutcomeEditor, { filterConfirmedESCOMappings } from './LearningOutcomeEditor';
import OccupationMatcher from './OccupationMatcher';
import TranslationEditor from './TranslationEditor';
import GenerationProgress from './GenerationProgress';
import CompressionDisplay from './CompressionDisplay';
import IscedFSelect from './IscedFSelect';
import HelpTooltip from './help/HelpTooltip';
import ValidationProgressModal from './ValidationProgressModal';
import ImprovementProgressModal from './ImprovementProgressModal';
import EnglishPreviewButton from './EnglishPreviewButton';
import AssessmentFrameworkEditor, { type AssessmentFramework } from './AssessmentFrameworkEditor';
import RecoverDraftModal from './RecoverDraftModal';
import ConflictResolutionModal from './ConflictResolutionModal';
import UnsavedChangesModal from './UnsavedChangesModal';
import { registerNavigationBlock } from '../lib/navigationBlock';
import {
  useAutosaveDraft,
  loadDraft,
  isDraftNewerThanDb,
  clearDraft,
  draftHasMeaningfulContent,
  type DraftPayload,
} from '../hooks/useAutosaveDraft';
import { readNdjsonStream } from '@/lib/ndjson-stream';

export default function StudyPlanForm() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Autosave draft (task #300, 2026-07-02): beskytter mot uheldig refresh/krasj
  // ved KI-generering og lengre redigering. Ren frontend-løsning — cache lever
  // i localStorage per plan-id (eller 'new' for nye planer).
  const draftPlanId = id || 'new';
  const [dbUpdatedAt, setDbUpdatedAt] = useState<string | null>(null);
  const [pendingDraft, setPendingDraft] = useState<DraftPayload<any> | null>(null);
  const [autosaveEnabled, setAutosaveEnabled] = useState(false);
  // Ulagrede endringer-modal (task #303, 2026-07-02)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  // Task #304 (2026-07-02): Hvilken path brukeren prøvde å navigere til.
  // Null = bruker klikket Avbryt (default naviger til /study-plans).
  // Ellers = ekstern nav (klikk på lenke i Layout) — vi navigerer til den path-en.
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  // Konflikt-håndtering (task #301): oppstår når backend returnerer 409
  // fordi en annen bruker har lagret endringer siden vi lastet planen
  const [conflictInfo, setConflictInfo] = useState<{
    currentUpdatedAt: string;
    expectedUpdatedAt: string;
    lastEditorName: string | null;
    pendingStatus: 'draft' | 'submitted';
  } | null>(null);

  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationStartTime, setValidationStartTime] = useState<number | null>(null);
  const [improvementStartTime, setImprovementStartTime] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [error, setError] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResponse | null>(null);
  const [escoValidation, setEscoValidation] = useState<{
    summary: { total: number; accepted: number; warnings: number; dropped: number; blocks_approval: boolean };
    dropped_count: number;
    warned_count: number;
    dropped: Array<{ outcomeIndex: number; outcomeType: string; outcomeText: string; escoUri: string; escoTitle: string; escoDescription?: string; escoSkillType?: string; reasons: string[] }>;
    warned: Array<{ outcomeIndex: number; outcomeType: string; outcomeText: string; escoUri: string; escoTitle: string; escoDescription?: string; escoSkillType?: string; reasons: string[] }>;
    blocks_approval: boolean;
  } | null>(null);
  const [resolvingEsco, setResolvingEsco] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [approving, setApproving] = useState(false);
  const [showSelfApproveModal, setShowSelfApproveModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [showSubmitForReviewModal, setShowSubmitForReviewModal] = useState(false);
  const [submissionJustification, setSubmissionJustification] = useState('');
  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [createdPlanId, setCreatedPlanId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [needsRevalidation, setNeedsRevalidation] = useState(false);
  const [validationRunThisSession, setValidationRunThisSession] = useState(false);
  // Task #348 (2026-07-07): fire-sone terskler fra DB
  const [thresholds, setThresholds] = useState<{
    auto_approve_threshold: number;
    self_approve_with_justification_threshold: number;
    manual_review_threshold: number;
    minimum_hours: number;
  }>({
    auto_approve_threshold: 80,
    self_approve_with_justification_threshold: 70,
    manual_review_threshold: 60,
    minimum_hours: 4,
  });
  const [originalFormData, setOriginalFormData] = useState<StudyPlanFormData | null>(null);
  const [studyPlanStatus, setStudyPlanStatus] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showValidationConfirm, setShowValidationConfirm] = useState(false);

  // AI Improvement suggestions
  const [improvements, setImprovements] = useState<any>(null);
  const [loadingImprovements, setLoadingImprovements] = useState(false);
  const [showImprovements, setShowImprovements] = useState(false);

  // AI Generation inputs
  const [generateDescription, setGenerateDescription] = useState('');
  const [generateTitle, setGenerateTitle] = useState('');
  const [generateECTS, setGenerateECTS] = useState<number | ''>('');
  const [generateHours, setGenerateHours] = useState<number | ''>('');
  const [generateEqfLevel, setGenerateEqfLevel] = useState('3');
  const [generateTargetGroup, setGenerateTargetGroup] = useState('');
  const [generateAdmissionReq, setGenerateAdmissionReq] = useState('');

  // Fase 1a (task #271): pre-analyse-resultat fra /api/ai/generate
  // Vises som info-boks etter at hovedgenerering er ferdig.
  const [preAnalysisResult, setPreAnalysisResult] = useState<any | null>(null);

  // Fase 2A/2B (tasks #273-274): komprimering + kompleksitetsvurdering
  const [compressionResult, setCompressionResult] = useState<any | null>(null);
  const [complexityResult, setComplexityResult] = useState<any | null>(null);

  // Fase 2C (task #276): fremdriftsindikator under generering
  const [generationPhases, setGenerationPhases] = useState<any[]>([]);

  const [formData, setFormData] = useState<StudyPlanFormData>({
    title: '',
    description: '',
    ects_credits: 0,
    estimated_hours: 0,
    learning_outcomes: {
      knowledge: [],
      skills: [],
      competence: [],
    },
    content_modules: [],
    assessment_criteria: '',
    metadata: {
      language: 'no',
      country: 'NO',
      eqf_level: '5',
      assessment_methods: [],
    },
    // New fields for enhanced validation
    target_group: '',
    admission_requirements: '',
    teaching_learning_methods: '',
    // kompetanseheving.no-kategorier (multi-select, første er primær)
    categories: [] as string[],
  });

  // Temporary input states for arrays
  const [moduleInput, setModuleInput] = useState('');
  const [assessmentMethodInput, setAssessmentMethodInput] = useState('');

  // Editing state for inline editing
  const [editingItem, setEditingItem] = useState<{field: string; index: number; value: string} | null>(null);

  // ESCO Skills state
  const [escoSearchQuery, setEscoSearchQuery] = useState('');
  const [escoSearchResults, setEscoSearchResults] = useState<any[]>([]);
  const [escoSearching, setEscoSearching] = useState(false);
  const [selectedEscoSkills, setSelectedEscoSkills] = useState<any[]>([]);

  // Per-outcome ESCO mappings
  const [outcomeEscoMappings, setOutcomeEscoMappings] = useState<OutcomeESCOMapping[]>([]);

  // v6: Kategoriadvarsler — utbytter som er feilkategorisert (kunnskap vs ferdighet)
  // Key format: "knowledge-0", "skills-1", "competence-2" etc.
  const [outcomeCategoryWarnings, setOutcomeCategoryWarnings] = useState<Record<string, any>>({});

  // Target group and admission requirements occupation mappings
  const [targetGroupOccupations, setTargetGroupOccupations] = useState<ESCOOccupationMatch[]>([]);
  const [admissionOccupations, setAdmissionOccupations] = useState<ESCOOccupationMatch[]>([]);

  // Module state
  const [modules, setModules] = useState<StudyPlanModule[]>([]);
  // Task #345 (2026-07-07): tracker originalModules så modul-endringer også
  // trigger hasUnsavedChanges + needsRevalidation. Ellers kan bruker endre
  // moduler etter godkjenning uten at Godkjenn-knappen forsvinner.
  const [originalModules, setOriginalModules] = useState<StudyPlanModule[]>([]);
  const [showModuleSection, setShowModuleSection] = useState(false);

  // Kategoriforslag basert på yrkesroller (v5.14)
  const [suggestedCategories, setSuggestedCategories] = useState<Array<{ category: string; score: number; matched: string[] }>>([]);

  // Fase 2.6 Blokk D: vurderingsramme (assessment_framework)
  // null = ingen ramme satt (bakoverkompatibel pass-through i backend)
  const [assessmentFramework, setAssessmentFramework] = useState<AssessmentFramework | null>(null);

  // Task #348: hent thresholds fra DB ved mount
  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch('/api/validation-rules/thresholds');
        if (res.ok) {
          const d = await res.json();
          if (d?.thresholds) setThresholds(d.thresholds);
        }
      } catch (e) {
        console.warn('Could not load thresholds, using defaults:', e);
      }
    })();
  }, []);

  useEffect(() => {
    // Slå opp kategoriforslag når yrkesrollene endres
    const uris = [
      ...targetGroupOccupations.map(o => o.uri).filter(Boolean),
      ...admissionOccupations.map(o => o.uri).filter(Boolean)
    ];
    if (uris.length === 0) {
      setSuggestedCategories([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch('/api/categories/suggest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ occupation_uris: uris })
        });
        const data = await res.json();
        if (!cancelled && data.success) {
          setSuggestedCategories(data.suggestions || []);
        }
      } catch (err) {
        console.warn('Category suggestion failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [targetGroupOccupations, admissionOccupations]);

  useEffect(() => {
    // Get current user
    const user = getUser();
    setCurrentUser(user);

    if (id) {
      loadStudyPlan(id);
    } else {
      // Ny plan — sjekk om det finnes lokalt utkast som skal tilbys
      // Task #302 (2026-07-02): kun tilby gjenoppretting hvis draft har
      // meningsfullt innhold (ikke bare tom init-state).
      const draft = loadDraft<any>(draftPlanId, user?.id);
      if (draft && isDraftNewerThanDb(draft, null) && draftHasMeaningfulContent(draft)) {
        setPendingDraft(draft);
      } else {
        // Ingen relevant draft — rens tom cache og aktiver autosave
        if (draft) clearDraft(draftPlanId, user?.id);
        setAutosaveEnabled(true);
      }
    }
  }, [id, draftPlanId]);

  // Autosave (task #300): pakk hele form-state i ett objekt og la hook-en
  // periodisk lagre det til localStorage. Autosave er kun aktiv når brukeren
  // har tatt stilling til evt. eksisterende draft (gjenopprett/forkast).
  const autosavePayload = {
    formData,
    outcomeEscoMappings,
    outcomeCategoryWarnings,
    selectedEscoSkills,
    targetGroupOccupations,
    admissionOccupations,
    preAnalysisResult,
    compressionResult,
    complexityResult,
  };
  const { clear: clearAutosaveDraft, discard: discardAutosaveDraft } = useAutosaveDraft({
    planId: draftPlanId,
    userId: currentUser?.id ?? null,
    data: autosavePayload,
    enabled: autosaveEnabled,
    dbUpdatedAt,
    // Task #347/#348 (2026-07-07): tidligere returnerte isDirty true for
    // ENHVER plan med innhold (draftHasMeaningfulContent). Autosave skrev
    // dermed cache konstant — også når state matcher DB. Det fikk
    // gjenopprett-modalen til å dukke opp etter hard-refresh uten reelle
    // endringer. Ny sjekk: skriv KUN til cache hvis
    //   (a) planen er ny (originalFormData=null) OG har innhold, ELLER
    //   (b) payload.formData avviker fra originalFormData
    // Da matcher cache "brukeren-har-gjort-endringer"-signalet.
    isDirty: (payload) => {
      const wrapped = {
        version: 1,
        planId: draftPlanId,
        timestamp: Date.now(),
        data: payload,
        meta: { dbUpdatedAt },
      };
      if (!draftHasMeaningfulContent(wrapped)) return false;
      // Ny plan (aldri lagret) → skriv cache når bruker har begynt å fylle inn
      if (!originalFormData) return true;
      // Eksisterende plan → kun hvis noe faktisk avviker fra siste DB-versjon
      const payloadForm = (payload as any)?.formData;
      if (!payloadForm) return false;
      return JSON.stringify(payloadForm) !== JSON.stringify(originalFormData);
    },
  });

  // Gjenopprett draft — brukeren har eksplisitt valgt å laste inn det lokale utkastet
  const handleRecoverDraft = () => {
    if (!pendingDraft) return;
    const d = pendingDraft.data || {};
    try {
      if (d.formData) setFormData(d.formData);
      if (Array.isArray(d.outcomeEscoMappings)) setOutcomeEscoMappings(d.outcomeEscoMappings);
      if (d.outcomeCategoryWarnings) setOutcomeCategoryWarnings(d.outcomeCategoryWarnings);
      if (Array.isArray(d.selectedEscoSkills)) setSelectedEscoSkills(d.selectedEscoSkills);
      if (Array.isArray(d.targetGroupOccupations)) setTargetGroupOccupations(d.targetGroupOccupations);
      if (Array.isArray(d.admissionOccupations)) setAdmissionOccupations(d.admissionOccupations);
      if (d.preAnalysisResult) setPreAnalysisResult(d.preAnalysisResult);
      if (d.compressionResult) setCompressionResult(d.compressionResult);
      if (d.complexityResult) setComplexityResult(d.complexityResult);
      toast.showSuccess('Utkast gjenopprettet fra nettleseren');
    } catch (err) {
      console.error('[Autosave] Gjenoppretting feilet:', err);
      toast.showError('Kunne ikke gjenopprette utkast');
    } finally {
      setPendingDraft(null);
      setAutosaveEnabled(true);
    }
  };

  // Forkast draft — brukeren vil beholde DB-versjonen
  const handleDiscardDraft = () => {
    clearDraft(draftPlanId, currentUser?.id);
    setPendingDraft(null);
    setAutosaveEnabled(true);
  };

  // Konflikt-håndtering (task #301): brukeren velger overskriv
  const handleConflictOverwrite = () => {
    const pendingStatus = conflictInfo?.pendingStatus || 'draft';
    setConflictInfo(null);
    // Send save igjen med force=true
    handleSave(pendingStatus, true);
  };

  // Konflikt-håndtering: brukeren velger å laste inn siste versjon
  const handleConflictReload = () => {
    setConflictInfo(null);
    if (id) {
      // Reload fra DB — utkastet i localStorage forblir intakt så brukeren
      // kan gjenopprette senere via "Vis ulagret utkast"-flyten
      loadStudyPlan(id);
    }
  };

  // Task #313: URL som ny-fane-lenke bruker. ?compare=1 signaliserer at
  // draft-recovery-modal skal hoppes over (rendret bruker skal se DB direkte).
  const comparisonUrl = id ? `/study-plans/${id}?compare=1` : '';

  // Konflikt-håndtering: brukeren avbryter helt
  const handleConflictCancel = () => {
    setConflictInfo(null);
  };

  // Registrer navigasjons-blokk (task #304, 2026-07-02) — Layout kaller
  // tryNavigate() ved klikk på Link/NavLink. Hvis vi har dirt, viser vi
  // modalen og returnerer false så Layout ikke navigerer selv.
  useEffect(() => {
    const hasDirt = () => id
      ? hasUnsavedChanges
      : draftHasMeaningfulContent({
          version: 1,
          planId: draftPlanId,
          timestamp: Date.now(),
          data: autosavePayload,
          meta: { dbUpdatedAt: null },
        });

    return registerNavigationBlock((attemptedPath) => {
      if (!hasDirt()) return true; // Rent skjema — la navigasjonen fortsette
      // Fang path-en, vis modal, blokk navigasjon
      setPendingNavigationPath(attemptedPath);
      setShowUnsavedChangesModal(true);
      return false;
    });
  }, [id, hasUnsavedChanges, autosavePayload, draftPlanId]);

  // Avbryt-knappen nederst i skjemaet (task #303, 2026-07-02).
  // Sjekk om skjemaet har ulagrede endringer FØR vi navigerer bort.
  // - For ny plan: "ulagret" = det finnes meningsfullt innhold i state
  // - For eksisterende plan: "ulagret" = hasUnsavedChanges (endret siden last)
  const handleCancelClick = () => {
    const hasDirt = id
      ? hasUnsavedChanges
      : draftHasMeaningfulContent({
          version: 1,
          planId: draftPlanId,
          timestamp: Date.now(),
          data: autosavePayload,
          meta: { dbUpdatedAt: null },
        });
    if (hasDirt) {
      setShowUnsavedChangesModal(true);
    } else {
      // Rent skjema — naviger. Bruk discard() så unmount-cleanup ikke
      // re-skriver dataen. Task #305.
      discardAutosaveDraft();
      navigate('/study-plans');
    }
  };

  const handleSaveAndLeave = async () => {
    setShowUnsavedChangesModal(false);
    const target = pendingNavigationPath || '/study-plans';
    setPendingNavigationPath(null);
    try {
      await handleSave('draft');
      // handleSave sletter cache selv ved suksess. Naviger etter en liten
      // stund så suksessmeldingen får vises kort.
      setTimeout(() => navigate(target), 800);
    } catch {
      // Feil vises via error-state; bruker forblir på siden
    }
  };

  const handleDiscardAndLeave = () => {
    setShowUnsavedChangesModal(false);
    const target = pendingNavigationPath || '/study-plans';
    setPendingNavigationPath(null);
    // Task #305: bruk discard() så unmount-cleanup ikke re-skriver dataen
    discardAutosaveDraft();
    navigate(target);
  };

  const handleKeepEditing = () => {
    setShowUnsavedChangesModal(false);
    setPendingNavigationPath(null);
  };

  // Track original ESCO data for change detection
  const [originalEscoData, setOriginalEscoData] = useState<{
    outcomeEscoMappings: OutcomeESCOMapping[];
    targetGroupOccupations: ESCOOccupationMatch[];
    admissionOccupations: ESCOOccupationMatch[];
    selectedEscoSkills: any[];
  } | null>(null);

  // Detect changes to form data and ESCO data
  useEffect(() => {
    if (!originalFormData || !studyPlanStatus) return;

    // Check if formData has changed from original
    const formDataChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);

    // Check if ESCO-related data has changed
    let escoDataChanged = false;
    if (originalEscoData) {
      escoDataChanged =
        JSON.stringify(outcomeEscoMappings) !== JSON.stringify(originalEscoData.outcomeEscoMappings) ||
        JSON.stringify(targetGroupOccupations) !== JSON.stringify(originalEscoData.targetGroupOccupations) ||
        JSON.stringify(admissionOccupations) !== JSON.stringify(originalEscoData.admissionOccupations) ||
        JSON.stringify(selectedEscoSkills) !== JSON.stringify(originalEscoData.selectedEscoSkills);
    }

    // Task #345: sjekk om moduler er endret (detaljerte moduler er
    // innholdsdata og skal invalidere validering ved endring)
    const modulesChanged = JSON.stringify(modules) !== JSON.stringify(originalModules);

    const hasChanged = formDataChanged || escoDataChanged || modulesChanged;

    setHasUnsavedChanges(hasChanged);

    // If plan is approved and data has changed, require revalidation
    if (studyPlanStatus === 'approved' && hasChanged) {
      setNeedsRevalidation(true);
      setValidationResult(null); // Clear old validation result since plan has changed
      setEscoValidation(null);
    }
  }, [formData, originalFormData, studyPlanStatus, outcomeEscoMappings, targetGroupOccupations, admissionOccupations, selectedEscoSkills, originalEscoData, modules, originalModules]);

  // Auto-hide success message after 10 seconds
  // Task #343 (2026-07-07): fjernet scroll-til-topp — meldingen vises nå
  // også over Lagre/Validering-knappene (i tillegg til toppen), så bruker
  // kan trykke KI-validering direkte etter save uten å scrolle.
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadStudyPlan = async (planId: string) => {
    setLoading(true);
    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Call email-service API to get study plan
      const response = await authenticatedFetch('/api/study-plans/get', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          study_plan_id: planId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to load study plan');
      }

      const data = result.study_plan;

      if (data) {
        const loadedFormData = {
          title: data.title,
          description: data.description,
          ects_credits: data.ects_credits,
          estimated_hours: data.estimated_hours,
          learning_outcomes: data.learning_outcomes,
          content_modules: data.content_modules,
          assessment_criteria: data.assessment_criteria,
          metadata: data.metadata,
          isced_f_code: data.isced_f_code || null,
          esco_skills: data.esco_skills || null,
          mc_metadata: data.mc_metadata || null,
          // New fields for enhanced validation
          target_group: data.target_group || '',
          admission_requirements: data.admission_requirements || '',
          teaching_learning_methods: data.teaching_learning_methods || '',
          categories: Array.isArray(data.categories) ? data.categories : [],
        };

        setFormData(loadedFormData);
        // Deep copy for originalFormData to avoid shared references with formData
        // Using JSON.parse/stringify to ensure exact same structure
        setOriginalFormData(JSON.parse(JSON.stringify(loadedFormData)));
        setStudyPlanStatus(data.status || '');
        setHasUnsavedChanges(false);

        // Fase 2.6 Blokk D: last inn eksisterende vurderingsramme
        if (data.assessment_framework && typeof data.assessment_framework === 'object') {
          setAssessmentFramework(data.assessment_framework as AssessmentFramework);
        } else {
          setAssessmentFramework(null);
        }

        // Autosave (task #300): registrer DB-timestamp og sjekk om lokalt utkast
        // er nyere enn siste lagrede versjon. Hvis ja, tilby gjenoppretting.
        const currentDbUpdatedAt: string | null = data.updated_at || null;
        setDbUpdatedAt(currentDbUpdatedAt);
        // Task #313 (2026-07-02): hvis URL har ?compare=1 (åpnet fra
        // konflikt-modalens "Vis DB i ny fane"), skal DB-versjonen vises
        // uendret uten å tilby draft-recovery.
        const isCompareMode = new URLSearchParams(window.location.search).get('compare') === '1';
        const draft = isCompareMode ? null : loadDraft<any>(planId, user.id);
        // Task #348: isDirty-callbacken i useAutosaveDraft (over) sikrer nå
        // at cache KUN skrives når payload.formData avviker fra
        // originalFormData. Dobbel-sikring her sammenligner faktisk innhold
        // i tilfelle en gammel cache fra før isDirty-fiksen ligger igjen.
        const draftFormData = draft?.data?.formData;
        const draftDiffersFromDb = draftFormData
          ? JSON.stringify(draftFormData) !== JSON.stringify(loadedFormData)
          : false;
        if (draft && isDraftNewerThanDb(draft, currentDbUpdatedAt) &&
            draftHasMeaningfulContent(draft) && draftDiffersFromDb) {
          setPendingDraft(draft);
          // autosaveEnabled forblir false inntil brukeren har valgt gjenopprett/forkast
        } else {
          // Ingen relevant draft — rydd bort utdatert eller tom cache og aktiver autosave
          // I compare-mode: rens IKKE cache og aktiver IKKE autosave (read-only visning)
          if (draft && !isCompareMode) clearDraft(planId, user.id);
          if (!isCompareMode) setAutosaveEnabled(true);
        }

        // If editing an approved plan, show version warning immediately
        // and allow editing of all fields including literature
        // Also clear old validation result - user must re-validate for new version
        console.log('[StudyPlanForm] Loading plan with status:', data.status);
        if (data.status === 'approved') {
          console.log('[StudyPlanForm] Setting needsRevalidation=true for approved plan');
          setNeedsRevalidation(true);
          setValidationResult(null); // Clear old validation - new version needs new validation
        } else {
          // Task #348 (2026-07-07): sjekk validation_invalidated_at ved lasting.
          // Hvis backend har markert planen som invalidated (f.eks. etter
          // litteratur-endring via discover-flyten), skal Godkjenn-knappene
          // være skjult inntil ny validering er kjørt.
          const isInvalidated = !!data.validation_invalidated_at;
          setNeedsRevalidation(isInvalidated);
          // Only load validation result for non-approved plans
          if (data.validation_feedback) {
            // Determine decision from feedback or score
            const decision = data.validation_decision ||
              data.validation_feedback?.decision ||
              (data.validation_score >= 80 ? 'approved' :
               data.validation_score >= 60 ? 'rejected_with_revisions' : 'rejected');
            setValidationResult({
              score: data.validation_score,
              feedback: data.validation_feedback,
              passed: data.validation_score >= 70,
              decision: decision,
            });
          }
        }

        // Initialize ESCO skills if present
        if (data.esco_skills && Array.isArray(data.esco_skills)) {
          setSelectedEscoSkills(data.esco_skills);
        }

        // Initialize per-outcome ESCO mappings from mc_metadata
        if (data.mc_metadata?.outcome_esco_mappings && Array.isArray(data.mc_metadata.outcome_esco_mappings)) {
          setOutcomeEscoMappings(data.mc_metadata.outcome_esco_mappings);
        }

        // Initialize target group and admission occupations from mc_metadata
        const loadedTargetGroupOccupations = data.mc_metadata?.target_group_occupations || [];
        const loadedAdmissionOccupations = data.mc_metadata?.admission_occupations || [];
        const loadedOutcomeEscoMappings = data.mc_metadata?.outcome_esco_mappings || [];

        if (Array.isArray(loadedTargetGroupOccupations)) {
          setTargetGroupOccupations(loadedTargetGroupOccupations);
        }
        if (Array.isArray(loadedAdmissionOccupations)) {
          setAdmissionOccupations(loadedAdmissionOccupations);
        }

        // Save original ESCO data for change detection
        setOriginalEscoData({
          outcomeEscoMappings: loadedOutcomeEscoMappings,
          targetGroupOccupations: loadedTargetGroupOccupations,
          admissionOccupations: loadedAdmissionOccupations,
          selectedEscoSkills: data.esco_skills || []
        });

        // Load modules for this study plan
        try {
          const modulesResponse = await authenticatedFetch(`/api/ai/modules/${planId}`);
          if (modulesResponse.ok) {
            const modulesData = await modulesResponse.json();
            if (modulesData.success && modulesData.modules) {
              setModules(modulesData.modules);
              setOriginalModules(JSON.parse(JSON.stringify(modulesData.modules)));
              if (modulesData.modules.length > 0) {
                setShowModuleSection(true);
              }
            }
          }
        } catch (e) {
          console.warn('Could not load modules:', e);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (
    status: 'draft' | 'submitted' = 'draft',
    forceOverwrite: boolean = false
  ) => {
    setSaving(true);
    setError('');

    try {
      // Validate ECTS alignment
      const ectsValidation = validateECTSAlignment(
        formData.ects_credits,
        formData.estimated_hours
      );
      if (!ectsValidation.valid) {
        throw new Error(ectsValidation.message);
      }

      // Validate learning outcomes
      const outcomesValidation = validateLearningOutcomes(formData.learning_outcomes);
      if (!outcomesValidation.valid) {
        throw new Error(outcomesValidation.errors.join(', '));
      }

      // Use custom auth instead of Supabase auth
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      if (!user?.study_association_id) {
        throw new Error('No study association found');
      }

      // Task #342: samle backend-returnert updated_at fra begge grener
      // (create + update) så vi kan synke expected_updated_at etter save
      let savedUpdatedAt: string | null = null;

      // Use id from URL or createdPlanId from first save
      const existingPlanId = id || createdPlanId;

      if (existingPlanId) {
        // Update existing via email-service API
        // Always save as draft first - we'll use submit endpoint for actual submission
        const saveStatus = studyPlanStatus === 'approved' ? 'draft' : 'draft';

        const response = await authenticatedFetch('/api/study-plans/update', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            study_plan_id: existingPlanId,
            title: formData.title,
            description: formData.description,
            ects_credits: formData.ects_credits,
            estimated_hours: formData.estimated_hours,
            learning_outcomes: formData.learning_outcomes,
            content_modules: formData.content_modules,
            assessment_criteria: formData.assessment_criteria,
            metadata: formData.metadata,
            isced_f_code: formData.isced_f_code,
            esco_skills: formData.esco_skills,
            mc_metadata: {
              ...formData.mc_metadata,
              // ESCO v2.2: Filter out unconfirmed medium/low tier matches
              outcome_esco_mappings: filterConfirmedESCOMappings(outcomeEscoMappings),
              target_group_occupations: targetGroupOccupations,
              admission_occupations: admissionOccupations,
            },
            // New fields for enhanced validation
            target_group: formData.target_group,
            admission_requirements: formData.admission_requirements,
            teaching_learning_methods: formData.teaching_learning_methods,
            categories: (formData.categories ?? []),
            // Fase 2.6 Blokk D: vurderingsramme (null = ingen håndhevelse)
            assessment_framework: assessmentFramework,
            status: saveStatus,
            // Optimistisk låsing (task #301): backend avviser 409 hvis
            // DB har blitt oppdatert av annen bruker siden vi lastet planen
            expected_updated_at: dbUpdatedAt,
            force: forceOverwrite,
          }),
        });

        if (response.status === 409) {
          // Konflikt: en annen bruker har lagret. Vis konflikt-modal.
          const conflictData = await response.json();
          setConflictInfo({
            currentUpdatedAt: conflictData.current_updated_at,
            expectedUpdatedAt: conflictData.expected_updated_at,
            lastEditorName: conflictData.last_editor_name || null,
            pendingStatus: status,
          });
          setSaving(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Failed to update study plan');
        }
        if (result.updated_at) savedUpdatedAt = result.updated_at;

        // If submitting, call the dedicated submit endpoint after saving
        if (status === 'submitted') {
          const submitResponse = await authenticatedFetch('/api/study-plans/submit', {
            method: 'POST',
            body: JSON.stringify({
              user_id: user.id,
              study_plan_id: existingPlanId,
            }),
          });

          if (!submitResponse.ok) {
            throw new Error(`HTTP error! status: ${submitResponse.status}`);
          }

          const submitResult = await submitResponse.json();
          if (!submitResult.success) {
            throw new Error(submitResult.error || 'Failed to submit study plan');
          }
        }
      } else {
        // Preflight: duplikat-tittel-sjekk. Blokkerer ikke, men lar brukeren bekrefte.
        try {
          const dupResp = await authenticatedFetch('/api/study-plans/check-title-duplicate', {
            method: 'POST',
            body: JSON.stringify({ user_id: user.id, title: formData.title }),
          });
          if (dupResp.ok) {
            const dupJson = await dupResp.json();
            if (Array.isArray(dupJson.duplicates) && dupJson.duplicates.length > 0) {
              const list = dupJson.duplicates
                .map((d: any) => `• ${d.title} (${d.status === 'draft' ? 'utkast' : d.status === 'approved' ? 'godkjent' : d.status}, sist endret ${new Date(d.updated_at).toLocaleDateString('nb-NO')})`)
                .join('\n');
              const confirmed = await confirm({
                title: 'Studieplan med samme tittel finnes fra før',
                message: `Det finnes allerede ${dupJson.duplicates.length} studieplan${dupJson.duplicates.length > 1 ? 'er' : ''} med tittelen "${formData.title}":\n\n${list}\n\nVil du redigere en av de eksisterende i stedet? Å opprette dupliserte studieplaner gjør systemet uoversiktlig.`,
                confirmLabel: 'Opprett ny likevel',
                cancelLabel: 'Avbryt',
                variant: 'warning',
              });
              if (!confirmed) {
                setSaving(false);
                return;
              }
            }
          }
        } catch (dupErr) {
          // Ikke fatal — fortsett med opprettelse selv om sjekken feiler
          console.warn('Duplicate title check failed:', dupErr);
        }

        // Create new via email-service API
        const response = await authenticatedFetch('/api/study-plans/create', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            title: formData.title,
            description: formData.description,
            ects_credits: formData.ects_credits,
            estimated_hours: formData.estimated_hours,
            learning_outcomes: formData.learning_outcomes,
            content_modules: formData.content_modules,
            assessment_criteria: formData.assessment_criteria,
            metadata: formData.metadata,
            isced_f_code: formData.isced_f_code,
            esco_skills: formData.esco_skills,
            mc_metadata: {
              ...formData.mc_metadata,
              // ESCO v2.2: Filter out unconfirmed medium/low tier matches
              outcome_esco_mappings: filterConfirmedESCOMappings(outcomeEscoMappings),
              target_group_occupations: targetGroupOccupations,
              admission_occupations: admissionOccupations,
            },
            // New fields for enhanced validation
            target_group: formData.target_group,
            admission_requirements: formData.admission_requirements,
            teaching_learning_methods: formData.teaching_learning_methods,
            categories: (formData.categories ?? []),
            // Fase 2.6 Blokk D: vurderingsramme (null = ingen håndhevelse)
            assessment_framework: assessmentFramework,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Failed to create study plan');
        }
        if (result.updated_at) savedUpdatedAt = result.updated_at;

        // If creating new, save the new ID and stay on page
        // API returns study_plan_id directly, not study_plan.id
        const newPlanId = result.study_plan_id || result.study_plan?.id;
        if (newPlanId) {
          setCreatedPlanId(newPlanId);

          // Update URL without full page reload
          window.history.replaceState(null, '', `/study-plans/${newPlanId}`);

          // Save modules if we have them and now have an ID
          if (modules.length > 0) {
            try {
              await authenticatedFetch(`/api/ai/modules/apply`, {
                method: 'POST',
                body: JSON.stringify({
                  study_plan_id: newPlanId,
                  modules: modules,
                }),
              });
            } catch (e) {
              console.warn('Could not save modules for new plan:', e);
            }
          }

          // Update state for the newly created plan
          // Deep copy to avoid shared references with formData
          setOriginalFormData(JSON.parse(JSON.stringify(formData)));
          setOriginalModules(JSON.parse(JSON.stringify(modules)));
          setHasUnsavedChanges(false);
          setStudyPlanStatus('draft');
        }
      }

      // Save modules if we have them and are updating an existing plan
      if (id && modules.length > 0) {
        try {
          // Delete existing modules and recreate
          await authenticatedFetch(`/api/ai/modules/${id}`, {
            method: 'DELETE',
          });
          await authenticatedFetch(`/api/ai/modules/apply`, {
            method: 'POST',
            body: JSON.stringify({
              study_plan_id: id,
              modules: modules,
            }),
          });
        } catch (e) {
          console.warn('Could not save modules:', e);
        }
      }

      // Update state after save
      if (id) {
        // Deep copy to avoid shared references with formData
        setOriginalFormData(JSON.parse(JSON.stringify(formData)));
        setOriginalModules(JSON.parse(JSON.stringify(modules)));
        setHasUnsavedChanges(false);

        // Update status in component state
        if (studyPlanStatus === 'approved') {
          setStudyPlanStatus('draft');
        }
      }

      // Show success message and decide whether to navigate
      if (status === 'submitted') {
        // Submitting - show message then navigate back
        setSuccessMessage('Studieplan sendt til studieforbund for godkjenning!');
        setTimeout(() => {
          navigate('/study-plans');
        }, 1500);
      } else if (id && status === 'draft') {
        // Saving draft on existing plan - stay on page with success message
        if (studyPlanStatus === 'approved') {
          setSuccessMessage('Endringer lagret som nytt utkast! Kjør KI-validering for å godkjenne som ny versjon.');
        } else {
          setSuccessMessage('Studieplan lagret som utkast');
        }
      } else if (!id && status === 'draft') {
        // Creating new plan as draft - stay on page
        setSuccessMessage('Studieplan opprettet! Du kan nå kjøre KI-validering.');
      }

      // Autosave (task #300): utkastet er nå lagret i DB — rydd bort lokal cache.
      // Ved 'submitted' rydder vi før navigasjonen skjer for å unngå at cache
      // ligger igjen mot en plan som brukeren allerede har sendt inn.
      clearAutosaveDraft();
      // For nye planer: cache lå under 'new'-nøkkelen, rydd også den så vi
      // ikke tilbyr gjenoppretting neste gang brukeren oppretter ny plan
      if (!id) clearDraft('new', currentUser?.id);

      // Optimistisk låsing (task #301 + #342): bruk backend-returnert
      // updated_at fra create/update-endpointet. Modules-save nedenfor kan
      // bumpe timestampen igjen, så vi henter fresh via POST /get som
      // fallback for å få siste verdi. Klientens `new Date()` matcher aldri
      // DB `NOW()` pga klokke-drift.
      let finalUpdatedAt: string | null = savedUpdatedAt;
      try {
        const planId = id || createdPlanId;
        if (planId) {
          const freshResp = await authenticatedFetch('/api/study-plans/get', {
            method: 'POST',
            body: JSON.stringify({ user_id: user.id, study_plan_id: planId }),
          });
          if (freshResp.ok) {
            const freshData = await freshResp.json();
            const freshTs = freshData?.study_plan?.updated_at;
            if (freshTs) finalUpdatedAt = freshTs;
          }
        }
      } catch (fallbackErr) {
        console.warn('Could not fetch fresh updated_at:', fallbackErr);
      }
      if (finalUpdatedAt) {
        setDbUpdatedAt(new Date(finalUpdatedAt).toISOString());
      }
      // Tøm eventuell konflikt-modal
      setConflictInfo(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Wrapper function that checks for unsaved changes before validation
  const handleValidateClick = () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Vennligst lagre studieplanen først før du kjører KI-validering.');
      return;
    }

    // If there are unsaved changes, show confirmation box
    if (hasUnsavedChanges) {
      setShowValidationConfirm(true);
      return;
    }

    // No unsaved changes, run validation directly
    handleValidate();
  };

  const handleValidate = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Vennligst lagre studieplanen først før du kjører KI-validering.');
      return;
    }

    // Preflight: blokker validering hvis moduler ikke summerer riktig
    // mot studieplanens totale KP / timer. KI-valideringen ser bare på
    // toppnivå-tallene, så avvik blir en blindsone uten denne sjekken.
    if (modules && modules.length > 0) {
      const modulesEcts = modules.reduce((s, m: any) => s + (Number(m.ects_credits) || 0), 0);
      const modulesHours = modules.reduce((s, m: any) => s + (Number(m.estimated_hours) || 0), 0);
      const planEcts = Number(formData.ects_credits) || 0;
      const planHours = Number(formData.estimated_hours) || 0;
      const ectsDelta = Math.abs(modulesEcts - planEcts);
      const hoursDelta = Math.abs(modulesHours - planHours);
      // Toleranse for float-avrunding
      const problems: string[] = [];
      if (ectsDelta > 0.01) {
        problems.push(
          `KP: modulene summerer til ${modulesEcts.toFixed(2)}, ` +
          `mens studieplanens totale er ${planEcts.toFixed(2)}`
        );
      }
      if (hoursDelta > 0.5) {
        problems.push(
          `Timer: modulene summerer til ${modulesHours}, ` +
          `mens studieplanens totale er ${planHours}`
        );
      }
      if (problems.length > 0) {
        setError(
          'KI-validering blokkert: modulene stemmer ikke med studieplanens totale. ' +
          problems.join('. ') + '. ' +
          'Rett dette først (endre modulverdiene eller studieplanens totale), ' +
          'ellers vurderer KI-en feil grunnlag.'
        );
        return;
      }
    }

    // Close confirmation box if open
    setShowValidationConfirm(false);
    setValidating(true);
    setValidationStartTime(Date.now());
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // FIRST: If there are unsaved changes, save them to study_plans table
      if (hasUnsavedChanges) {
        const updateResponse = await authenticatedFetch('/api/study-plans/update', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            study_plan_id: planId,
            title: formData.title,
            description: formData.description,
            ects_credits: formData.ects_credits,
            estimated_hours: formData.estimated_hours,
            learning_outcomes: formData.learning_outcomes,
            content_modules: formData.content_modules,
            assessment_criteria: formData.assessment_criteria,
            metadata: formData.metadata,
            // New fields for enhanced validation
            target_group: formData.target_group,
            admission_requirements: formData.admission_requirements,
            teaching_learning_methods: formData.teaching_learning_methods,
            categories: (formData.categories ?? []),
            isced_f_code: formData.isced_f_code,
            esco_skills: formData.esco_skills,
            mc_metadata: {
              ...formData.mc_metadata,
              // ESCO v2.2: Filter out unconfirmed medium/low tier matches
              outcome_esco_mappings: filterConfirmedESCOMappings(outcomeEscoMappings),
              target_group_occupations: targetGroupOccupations,
              admission_occupations: admissionOccupations,
            },
            // Fase 2.6 Blokk D: vurderingsramme (null = ingen håndhevelse)
            assessment_framework: assessmentFramework,
            status: null, // Don't change status
          }),
        });

        if (!updateResponse.ok) {
          throw new Error(`Failed to save changes: ${updateResponse.status}`);
        }

        const updateResult = await updateResponse.json();
        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Failed to save changes');
        }

        // Update original form data after successful save
        // Deep copy to avoid shared references with formData
        setOriginalFormData(JSON.parse(JSON.stringify(formData)));
        setOriginalModules(JSON.parse(JSON.stringify(modules)));
        setHasUnsavedChanges(false);
      }

      // SECOND: Validate study plan with automatic English translation
      // The new endpoint generates English translation if missing, then validates both languages
      const response = await authenticatedFetch(`/api/study-plans/${planId}/validate-with-translation`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        // Check if it's a translation quality failure
        if (result.translation_quality && result.translation_quality.score < 60) {
          setError(`Engelsk oversettelse oppfyller ikke kvalitetskrav (score: ${result.translation_quality.score}/100). ${result.suggestion || ''}`);
          // Still show the partial result for debugging
          if (result.norwegian_score) {
            setValidationResult({
              score: result.norwegian_score,
              feedback: { translation_quality: result.translation_quality } as ValidationFeedback,
              passed: result.norwegian_passed,
              decision: 'rejected_with_revisions'
            });
          }
          return;
        }
        throw new Error(result.error || 'Validation failed');
      }

      setValidationResult(result.validation_result);
      setEscoValidation(result.esco_validation || null);
      setNeedsRevalidation(false); // Validation done, can approve now
      setValidationRunThisSession(true); // Mark that validation was run in this session

      // ESCO-kvalitetssjekk kan ha droppet noen koblinger fra mc_metadata på backend.
      // Synkroniser frontend-state slik at chip-listen reflekterer faktisk lagret tilstand.
      if (result.esco_validation?.dropped?.length > 0) {
        const droppedUris = new Set(
          result.esco_validation.dropped.map((d: any) => d.escoUri)
        );
        setOutcomeEscoMappings(prev =>
          prev
            .map(m => ({
              ...m,
              escoSkills: (m.escoSkills || []).filter(s => !droppedUris.has(s.uri))
            }))
            .filter(m => (m.escoSkills || []).length > 0)
        );
      }

      // If translation was generated, update local form data with English fields
      // Task #314 (2026-07-02): også oppdater originalFormData-baseline med
      // engelske felter, ellers ser useEffect på linje 410 dette som "endring"
      // og setter hasUnsavedChanges=true → Godkjenn-knapp skjules.
      if (result.translation) {
        setFormData(prev => {
          const updated = {
            ...prev,
            title_en: result.translation.title_en,
            description_en: result.translation.description_en,
            target_group_en: result.translation.target_group_en,
            admission_requirements_en: result.translation.admission_requirements_en,
            teaching_learning_methods_en: result.translation.teaching_learning_methods_en,
            assessment_criteria_en: result.translation.assessment_criteria_en,
            learning_outcomes_en: result.translation.learning_outcomes_en,
            content_modules_en: result.translation.content_modules_en,
          };
          // Bumperer baseline så useEffect ikke ser engelske felter som "unsaved"
          setOriginalFormData(JSON.parse(JSON.stringify(updated)));
          return updated;
        });
      }

      // Task #314/316: Backend har lagret score/decision/oversettelse — bump
      // dbUpdatedAt-state slik at neste save ikke feiler med 409. Bruk backend
      // sin faktiske timestamp (task #316), ikke klient-tid som gir
      // millisekund-mismatch mot DB.
      if (result.updated_at) {
        setDbUpdatedAt(new Date(result.updated_at).toISOString());
      } else {
        setDbUpdatedAt(new Date().toISOString());
      }
      setHasUnsavedChanges(false);

      // Don't reload - it would overwrite the form with data from study_plan_versions
      // Just update validation result and keep the current form data

      // Show success message with translation info — kun vis score hvis vi faktisk har et gyldig tall
      const translationScore = result.translation_quality?.score;
      const hasValidScore = typeof translationScore === 'number' && !isNaN(translationScore);
      setSuccessMessage(hasValidScore
        ? `KI-validering fullført! Oversettelse kvalitetsscore: ${translationScore}/100`
        : 'KI-validering fullført!');
      // Clear any previous improvements when validation is re-run
      setImprovements(null);
      setShowImprovements(false);
      // Task #343 (2026-07-07): scroll til toppen så bruker ser
      // validation-resultatet direkte. For save-flyten er dette bevisst
      // droppet — success-melding vises rett over knappene istedenfor.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError('Feil ved validering: ' + err.message);
    } finally {
      setValidating(false);
    }
  };

  // Fetch AI improvement suggestions
  const handleFetchImprovements = async () => {
    const planId = id || createdPlanId;
    if (!planId || !validationResult) {
      return;
    }

    // Hvis det finnes ulagrede endringer: spør bruker om å lagre først
    // (samme mønster som handleValidate — se linje ~650)
    if (hasUnsavedChanges) {
      const proceed = await confirm({
        title: 'Ulagrede endringer',
        message:
          'Du har ulagrede endringer i studieplanen. KI-forslag må baseres på lagret ' +
          'versjon for å være pålitelige.\n\nVil du lagre endringene nå og deretter ' +
          'kjøre KI-forslag?',
        confirmLabel: 'Lagre og kjør forslag',
        cancelLabel: 'Avbryt',
        variant: 'warning',
      });
      if (!proceed) return;
    }

    setLoadingImprovements(true);
    setImprovementStartTime(Date.now());
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Auto-lagre hvis endringer
      if (hasUnsavedChanges) {
        const updateResponse = await authenticatedFetch('/api/study-plans/update', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            study_plan_id: planId,
            title: formData.title,
            description: formData.description,
            ects_credits: formData.ects_credits,
            estimated_hours: formData.estimated_hours,
            learning_outcomes: formData.learning_outcomes,
            content_modules: formData.content_modules,
            assessment_criteria: formData.assessment_criteria,
            metadata: formData.metadata,
            target_group: formData.target_group,
            admission_requirements: formData.admission_requirements,
            teaching_learning_methods: formData.teaching_learning_methods,
            categories: (formData.categories ?? []),
            isced_f_code: formData.isced_f_code,
            esco_skills: formData.esco_skills,
            mc_metadata: {
              ...formData.mc_metadata,
              outcome_esco_mappings: filterConfirmedESCOMappings(outcomeEscoMappings),
              target_group_occupations: targetGroupOccupations,
              admission_occupations: admissionOccupations,
            },
            // Fase 2.6 Blokk D: vurderingsramme (null = ingen håndhevelse)
            assessment_framework: assessmentFramework,
            status: null,
          }),
        });
        if (!updateResponse.ok) {
          throw new Error(`Kunne ikke lagre endringene: ${updateResponse.status}`);
        }
        const updateResult = await updateResponse.json();
        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Kunne ikke lagre endringene');
        }
        setOriginalFormData(JSON.parse(JSON.stringify(formData)));
        setHasUnsavedChanges(false);
      }

      const response = await authenticatedFetch('/api/study-plans/improve', {
        method: 'POST',
        body: JSON.stringify({
          study_plan_id: planId,
          validation_feedback: validationResult.feedback
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate improvements');
      }

      // Include suggested_outcome_skills + suggested_categories in improvements object
      const improvementsWithSkills = {
        ...result.improvements,
        suggested_outcome_skills: result.suggested_outcome_skills || null,
        suggested_categories: Array.isArray(result.suggested_categories) ? result.suggested_categories : []
      };
      setImprovements(improvementsWithSkills);
      setShowImprovements(true);

      // Task #318: Backend har lagret data (esco-forslag, mc_metadata) og bumpet
      // updated_at. Sync dbUpdatedAt så neste save ikke feiler med 409.
      if (result.updated_at) {
        setDbUpdatedAt(new Date(result.updated_at).toISOString());
      }
    } catch (err: any) {
      setError('Feil ved generering av forbedringsforslag: ' + err.message);
    } finally {
      setLoadingImprovements(false);
    }
  };

  // Apply a single improvement to a field and remove it from suggestions
  const applyImprovement = (field: string, value: any, subField?: string, index?: number) => {
    // Apply the change to formData
    if (field === 'learning_outcomes' && subField && typeof index === 'number') {
      // Deep copy to avoid mutating originalFormData - preserve exact structure
      const outcomes = JSON.parse(JSON.stringify(formData.learning_outcomes || {}));
      const typeKey = subField as 'knowledge' | 'skills' | 'competence';
      if (outcomes[typeKey] && outcomes[typeKey][index] !== undefined) {
        outcomes[typeKey][index] = value;
        setFormData({...formData, learning_outcomes: outcomes});
        setHasUnsavedChanges(true);
        setNeedsRevalidation(true);
      }
    } else if (field === 'content_modules' && typeof index === 'number') {
      const modules = [...(formData.content_modules || [])];
      if (modules[index] !== undefined) {
        modules[index] = value;
        setFormData({...formData, content_modules: modules});
        setHasUnsavedChanges(true);
        setNeedsRevalidation(true);
      }
    } else if (field === 'metadata' && subField) {
      // Handle metadata fields - each has different storage location
      if (subField === 'esco_skills') {
        // ESCO skills are stored in separate state and use a different format
        // Convert string array to ESCO skill objects
        const escoSkillObjects = (value || []).map((skillName: string, idx: number) => ({
          uri: `ai-suggested-${idx}`,
          title: skillName,
          description: 'KI-foreslått ferdighet'
        }));
        setSelectedEscoSkills(escoSkillObjects);
      } else if (subField === 'eqf_level') {
        // EQF level is inside metadata
        setFormData({
          ...formData,
          metadata: { ...formData.metadata, eqf_level: value as any }
        });
      } else if (subField === 'isced_code') {
        // ISCED code is a top-level field (not in metadata)
        setFormData({
          ...formData,
          isced_f_code: value
        });
      } else if (subField === 'keywords') {
        // Keywords - store in metadata.keywords array
        setFormData({
          ...formData,
          metadata: { ...formData.metadata, keywords: value as string[] }
        });
      }
      setHasUnsavedChanges(true);
      setNeedsRevalidation(true);
    } else {
      setFormData({...formData, [field]: value});
      setHasUnsavedChanges(true);
      setNeedsRevalidation(true);
    }

    // Remove the applied improvement from the suggestions list
    if (improvements) {
      const updatedImprovements = {...improvements};

      if (field === 'learning_outcomes' && subField && typeof index === 'number') {
        // Mark specific learning outcome as applied
        if (updatedImprovements.learning_outcomes?.[subField]) {
          updatedImprovements.learning_outcomes[subField] = updatedImprovements.learning_outcomes[subField].map((item: any) =>
            item.index === index ? {...item, needs_improvement: false} : item
          );
        }
      } else if (field === 'content_modules' && typeof index === 'number') {
        // Mark specific content module as applied
        if (updatedImprovements.content_modules) {
          updatedImprovements.content_modules = updatedImprovements.content_modules.map((item: any) =>
            item.index === index ? {...item, needs_improvement: false} : item
          );
        }
      } else if (field === 'metadata' && subField) {
        // Mark metadata field as applied
        if (updatedImprovements.metadata?.[subField]) {
          updatedImprovements.metadata[subField] = {...updatedImprovements.metadata[subField], needs_improvement: false};
        }
      } else {
        // Simple field - mark as no longer needing improvement
        if (updatedImprovements[field]) {
          updatedImprovements[field] = {...updatedImprovements[field], needs_improvement: false};
        }
      }

      setImprovements(updatedImprovements);
    }

    // Task #332 (2026-07-03): bruk toast istedet for setSuccessMessage så
    // ingen ny DOM-boks legges til øverst på siden. Det unngår at brukeren
    // "hopper" oppover hver gang de trykker "Bruk forslag" i KI-forslag-listen.
    toast.showSuccess('Forslag brukt — husk å lagre og kjøre ny validering');
  };

  const handleGenerate = async () => {
    if (!generateDescription.trim()) {
      toast.showWarning('Vennligst beskriv studieplanen du ønsker å generere');
      return;
    }

    // Validate minimum hours (required for generation)
    const hours = generateHours || (generateECTS ? Number(generateECTS) * 25 : 0);
    if (!hours || hours < 4) {
      toast.showWarning('Vennligst angi estimerte timer (minimum 4 timer for Kompetanseattest)');
      return;
    }

    setGenerating(true);
    setError('');
    // Fase 2C: reset fremdriftsindikator + komprimeringsresultater
    setGenerationPhases([]);
    setCompressionResult(null);
    setComplexityResult(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await authenticatedFetch('/api/ai/generate', {
        method: 'POST',
        body: JSON.stringify({
          description: generateDescription,
          title: generateTitle || undefined,
          ects_credits: generateECTS || undefined,
          estimated_hours: hours,
          eqf_level: generateEqfLevel || '5',
          target_group: generateTargetGroup || undefined,
          admission_requirements: generateAdmissionReq || undefined,
        }),
      });

      // Fase 2C: NDJSON-stream for fremdriftsindikator
      const result = await readNdjsonStream(response, (event) => {
        if (event.phase !== 'result' && event.phase !== 'error') {
          // Akkumuler fase-events for visning
          setGenerationPhases(prev => [...prev, event as any]);
        }
      });

      const {
        generated_plan,
        esco_suggestions,
        outcome_esco_mappings,
        outcome_category_warnings,
        target_group_occupations,
        pre_analysis,
        compression,
        complexity_check
      } = result;

      console.log('AI Generated Plan:', generated_plan);
      console.log('ESCO Suggestions:', esco_suggestions);
      console.log('Outcome ESCO Mappings:', outcome_esco_mappings);
      console.log('Target Group Occupations:', target_group_occupations);
      // Fase 1a: pre-analyse-data for info-boks-visning
      if (pre_analysis) {
        console.log('[Pre-analyse] strategi:', pre_analysis.strategy, '| fagord:', pre_analysis.domain_terms?.length || 0);
        setPreAnalysisResult(pre_analysis);
      }
      // Fase 2A: komprimering
      if (compression) {
        console.log('[Komprimering] raw:', compression.raw_count, '→ unique:', compression.unique_count, '(merged:', compression.merged_count, ')');
        setCompressionResult(compression);
      }
      // Fase 2B: kompleksitet
      if (complexity_check) {
        console.log('[Kompleksitet]', complexity_check.status, '| ratio:', complexity_check.ratio);
        setComplexityResult(complexity_check);
      }

      // Fill form with generated data
      const newFormData: StudyPlanFormData = {
        title: generated_plan.title || '',
        description: generated_plan.description || '',
        ects_credits: generated_plan.ects_credits || 0,
        estimated_hours: generated_plan.estimated_hours || 0,
        learning_outcomes: {
          knowledge: generated_plan.learning_outcomes?.knowledge || [],
          skills: generated_plan.learning_outcomes?.skills || [],
          competence: generated_plan.learning_outcomes?.competence || [],
        },
        content_modules: generated_plan.content_modules?.map((m: any) => {
          // Task #339 (2026-07-06): fix "undefinedt" når m.hours mangler
          if (typeof m === 'string') return m;
          const hoursPart = (m.hours != null && m.hours !== '') ? ` (${m.hours}t)` : '';
          return `${m.title}: ${m.description}${hoursPart}`;
        }) || [],
        assessment_criteria: generated_plan.assessment_criteria || '',
        metadata: {
          language: generated_plan.metadata?.language || 'no',
          country: generated_plan.metadata?.country || 'NO',
          eqf_level: generated_plan.metadata?.eqf_level || generateEqfLevel || '5',
          assessment_methods: generated_plan.metadata?.assessment_methods || [],
        },
        // New fields from enhanced generation
        target_group: generated_plan.target_group || generateTargetGroup || '',
        admission_requirements: generated_plan.admission_requirements || generateAdmissionReq || '',
        teaching_learning_methods: generated_plan.teaching_learning_methods || '',
        // ISCED-F code from AI generation
        isced_f_code: generated_plan.isced_f_code || null,
        // Kategorier foreslått av KI/regelbasert (v5.14)
        categories: Array.isArray(generated_plan.categories) ? generated_plan.categories : [],
      };

      // Automatically add ESCO skills if suggestions were returned
      if (esco_suggestions && esco_suggestions.length > 0) {
        newFormData.esco_skills = esco_suggestions.map((skill: any) => ({
          uri: skill.uri,
          code: skill.code,
          title: skill.title,
          description: skill.description || '',
          skillType: skill.skillType || 'skill/competence',
          reuseLevel: skill.reuseLevel || 'cross-sectoral',
          isced_f: skill.isced_f || null,
        }));
        console.log('Added', esco_suggestions.length, 'ESCO skills automatically');
      }

      console.log('New Form Data:', newFormData);
      setFormData(newFormData);

      // Set ESCO mappings for learning outcomes (from NB-SBERT matching)
      if (outcome_esco_mappings && Object.keys(outcome_esco_mappings).length > 0) {
        const mappings: OutcomeESCOMapping[] = [];
        for (const [key, skills] of Object.entries(outcome_esco_mappings)) {
          const [type, indexStr] = key.split('-');
          // 2026-07-02: v6-auto-bekrefting fjernet. Tidligere ble ALLE GPT-vurderte
          // kandidater (gptScore satt) automatisk confirmed=true, som bypasset
          // 0.90-terskelen for auto-godkjenning (high tier). Nå avgjør tier-check
          // alene: kun kandidater i high tier (≥0.90) auto-inkluderes, resten
          // krever brukerbekreftelse. Bevarer eksisterende confirmed=true fra DB
          // (relevant ved re-load av lagret plan).
          const skillsArr = (skills as ESCOSkillMatch[]).map(s => ({
            ...s,
            confirmed: s.confirmed === true
          }));
          mappings.push({
            outcomeIndex: parseInt(indexStr),
            outcomeType: type as 'knowledge' | 'skills' | 'competence',
            escoSkills: skillsArr
          });
        }
        setOutcomeEscoMappings(mappings);
        console.log('Set', mappings.length, 'ESCO outcome mappings');
      }

      // Set ESCO occupations for target group
      if (target_group_occupations && target_group_occupations.length > 0) {
        setTargetGroupOccupations(target_group_occupations);
        console.log('Set', target_group_occupations.length, 'target group occupations');
      }

      // v6: Set kategoriadvarsler hvis noen utbytter er feilkategorisert
      if (outcome_category_warnings && Object.keys(outcome_category_warnings).length > 0) {
        setOutcomeCategoryWarnings(outcome_category_warnings);
        console.log('Set', Object.keys(outcome_category_warnings).length, 'category warnings');
      } else {
        setOutcomeCategoryWarnings({});
      }

      setShowGenerateModal(false);
      setGenerateDescription('');
      setGenerateTitle('');
      setGenerateECTS('');
      setHasUnsavedChanges(true);

      const occCount = target_group_occupations?.length || 0;
      const outcomeCount = outcome_esco_mappings ? Object.keys(outcome_esco_mappings).length : 0;
      if (outcomeCount > 0) {
        setSuccessMessage(`Studieplan generert! ${occCount} yrkesroller og ESCO-forslag for ${outcomeCount} læringsutbytter er satt. Se gjennom og juster etter behov.`);
      } else {
        setSuccessMessage(`Studieplan generert! ${occCount} yrkesroller er automatisk koblet. For ESCO-koblinger på læringsutbytter: bla ned til metadata-seksjonen og klikk "KI-foreslå ESCO for læringsutbytter".`);
      }
    } catch (err: any) {
      setError('Feil ved generering: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Kan ikke godkjenne en studieplan som ikke er lagret ennå');
      return;
    }

    if (needsRevalidation) {
      setError('Du har gjort endringer som krever ny KI-validering. Vennligst kjør KI-validering før godkjenning.');
      return;
    }

    if (!validationResult) {
      setError('Du må kjøre KI-validering før godkjenning');
      return;
    }

    // This function is only called when score >= 70, so no need to check for low score
    setApproving(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Build complete comment with KI-validation result + admin notes
      const fullComment = `KI-VALIDERING:
Score: ${Number(validationResult?.score || 0).toFixed(1)}%
Tilbakemelding: ${validationResult?.feedback?.summary || 'Ingen tilbakemelding'}

${approveComment.trim() ? `MERKNADER:\n${approveComment.trim()}` : ''}`;

      // Approve (which creates version snapshot from study_plans)
      const response = await authenticatedFetch('/api/study-plans/approve', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: user.id,
          study_plan_id: planId,
          change_comment: fullComment,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to approve study plan');
      }

      setSuccessMessage('Studieplan godkjent! Godkjenningslenke er generert.');
      setShowApproveModal(false);
      setApproveComment('');

      // Navigate back to study plans list
      navigate('/study-plans');
    } catch (err: any) {
      setError('Feil ved godkjenning: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleSelfApprove = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      toast.showWarning('Kan ikke godkjenne en studieplan som ikke er lagret ennå');
      return;
    }

    if (needsRevalidation) {
      toast.showWarning('Du har gjort endringer som krever ny KI-validering. Vennligst kjør KI-validering før godkjenning.');
      return;
    }

    if (!validationResult || validationResult.score < 70) {
      toast.showWarning('Studieplanen må ha en KI-score på minst 70% for å kunne selvgodkjennes');
      return;
    }

    setApproving(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Self-approve via API (member approves their own plan)
      const response = await authenticatedFetch('/api/study-plans/approve', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: user.id, // Member approves their own plan
          study_plan_id: planId,
          change_comment: `Selvgodkjent basert på kvalitetssikringssystemets valideringsresultat (Resultat ${Number(validationResult.score).toFixed(1)} %)`,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to self-approve study plan');
      }

      toast.showSuccess('Studieplan selvgodkjent! Godkjenningslenke er generert.');
      setShowSelfApproveModal(false);

      // Navigate back to study plans list
      navigate('/study-plans');
    } catch (err: any) {
      setError(err.message);
      toast.showError('Feil ved selvgodkjenning: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleOverrideApprove = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Kan ikke godkjenne en studieplan som ikke er lagret ennå');
      return;
    }

    if (needsRevalidation) {
      setError('Du har gjort endringer som krever ny KI-validering. Vennligst kjør KI-validering før godkjenning.');
      return;
    }

    if (!validationResult) {
      setError('Du må kjøre KI-validering før godkjenning');
      return;
    }

    if (overrideReason.trim().length < 30) {
      setError('Begrunnelsen må være minst 30 tegn');
      return;
    }

    setApproving(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Build override comment with full context
      const fullComment = `KI-VALIDERING OVERPRØVET:
Score: ${Number(validationResult?.score || 0).toFixed(1)}%
KI-tilbakemelding: ${validationResult?.feedback?.summary || 'Ingen tilbakemelding'}

BEGRUNNELSE FOR OVERPRØVING:
${overrideReason.trim()}`;

      // Approve with override flag
      const response = await authenticatedFetch('/api/study-plans/approve', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: user.id,
          study_plan_id: planId,
          change_comment: fullComment,
          override_reason: overrideReason.trim(),
          is_ai_override: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to override approve study plan');
      }

      setSuccessMessage('Studieplan godkjent med KI-overprøving! Godkjenningslenke er generert.');
      setShowOverrideModal(false);
      setOverrideReason('');

      // Navigate back to study plans list
      navigate('/study-plans');
    } catch (err: any) {
      setError('Feil ved godkjenning: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  // Handle submit for review with justification (for rejected_with_revisions plans)
  const handleSubmitForReview = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Kan ikke sende inn en studieplan som ikke er lagret ennå');
      return;
    }

    if (submissionJustification.trim().length < 50) {
      setError('Begrunnelsen må være minst 50 tegn');
      return;
    }

    setSubmittingForReview(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      const response = await authenticatedFetch('/api/study-plans/submit-for-review', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          study_plan_id: planId,
          justification: submissionJustification.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit for review');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit for review');
      }

      setSuccessMessage('Studieplan sendt inn for manuell vurdering!');
      setShowSubmitForReviewModal(false);
      setSubmissionJustification('');

      // Navigate back to study plans list
      navigate('/study-plans');
    } catch (err: any) {
      setError('Feil ved innsending: ' + err.message);
    } finally {
      setSubmittingForReview(false);
    }
  };

  const addModule = () => {
    if (!moduleInput.trim()) return;
    setFormData({
      ...formData,
      content_modules: [...formData.content_modules, moduleInput.trim()],
    });
    setModuleInput('');
  };

  const removeModule = (index: number) => {
    setFormData({
      ...formData,
      content_modules: formData.content_modules.filter((_, i) => i !== index),
    });
  };

  const updateModule = (index: number, value: string) => {
    if (!value.trim()) return;
    const newModules = [...formData.content_modules];
    newModules[index] = value.trim();
    setFormData({
      ...formData,
      content_modules: newModules,
    });
  };

  const addAssessmentMethod = () => {
    if (!assessmentMethodInput.trim()) return;
    setFormData({
      ...formData,
      metadata: {
        ...formData.metadata,
        assessment_methods: [...(formData.metadata.assessment_methods || []), assessmentMethodInput.trim()],
      },
    });
    setAssessmentMethodInput('');
  };

  const updateAssessmentMethod = (index: number, value: string) => {
    if (!value.trim()) return;
    const newMethods = [...(formData.metadata.assessment_methods || [])];
    newMethods[index] = value.trim();
    setFormData({
      ...formData,
      metadata: {
        ...formData.metadata,
        assessment_methods: newMethods,
      },
    });
  };

  // Auto-foreslå ESCO-ferdigheter for læringsutbytter — kalles fra både Læringsutbytte-
  // og ESCO-ferdigheter-seksjonen. Fungerer uten lagring (in-memory data).
  const [autoSuggestingEscoForOutcomes, setAutoSuggestingEscoForOutcomes] = useState(false);

  const handleAutoSuggestEscoForOutcomes = async () => {
    const outs = formData.learning_outcomes || { knowledge: [], skills: [], competence: [] };
    const totalOutcomes = (outs.knowledge?.length || 0) + (outs.skills?.length || 0) + (outs.competence?.length || 0);
    if (totalOutcomes === 0) {
      toast.showWarning('Du må ha minst ett læringsutbytte før KI kan foreslå ESCO-ferdigheter.');
      return;
    }
    if (outcomeEscoMappings.length > 0) {
      const ok = await confirm({
        title: 'Erstatt eksisterende ESCO-koblinger?',
        message: 'Du har allerede ESCO-koblinger på noen læringsutbytter. KI-forslagene vil erstatte alle eksisterende koblinger.',
        confirmLabel: 'Erstatt',
        cancelLabel: 'Avbryt',
        variant: 'warning'
      });
      if (!ok) return;
    }

    setAutoSuggestingEscoForOutcomes(true);
    try {
      const res = await authenticatedFetch('/api/esco/local/match-outcomes', {
        method: 'POST',
        body: JSON.stringify({
          learning_outcomes: outs,
          context: `${formData.title || ''} - ${formData.description || ''}`,
          studyPlanContext: {
            title: formData.title,
            description: formData.description,
            eqfLevel: parseInt(formData.metadata?.eqf_level || '5', 10),
            ectsCredits: formData.ects_credits,
            estimatedHours: formData.estimated_hours
          }
        })
      });
      if (!res.ok) throw new Error('Kunne ikke hente ESCO-forslag');
      const data = await res.json();
      if (!data.success || !data.results) throw new Error(data.error || 'Tomt svar fra KI');

      const newMappings: OutcomeESCOMapping[] = [];
      const newWarnings: Record<string, any> = {};
      (['knowledge', 'skills', 'competence'] as const).forEach(type => {
        const arr = data.results[type];
        if (!Array.isArray(arr)) return;
        arr.forEach((r: any, i: number) => {
          const skills = r.esco_skills || r.matches || [];
          if (skills.length > 0) {
            newMappings.push({
              outcomeIndex: i,
              outcomeType: type,
              escoSkills: skills.map((m: any) => ({
                uri: m.uri,
                code: m.code,
                title: m.title,
                description: m.description,
                skillType: m.skill_type,
                score: m.confidence || m.score,
                confidence: m.confidence || m.score,
                matchedVerb: m.matched_verb,
                gptScore: m.gptScore,
                gptReasoning: m.gptReasoning,
                matchMethod: m.matchMethod,
                // v6 (#6): Auto-bekreft matches som har passert GPT med domain_match=same.
                // GPT-batch returnerer kun matches med gptScore >= 0.65 + domain_match=same,
                // så de er allerede semantisk validert. Tidligere krevdes manuell bekreftelse
                // for medium-tier (0.50-0.69) → 75% av matches forsvant ved save.
                confirmed: (m.gptScore !== undefined && m.gptScore !== null) || m.matchMethod?.includes('gpt')
              })),
              bloomAnalysis: r.bloom || (r.bloom_level ? {
                level: r.bloom_level,
                levelName: r.bloom_name,
                matchedVerb: r.bloom_verb
              } : undefined)
            });
          }
          // v6: Plukk opp kategoriadvarsler per utbytte
          if (r.category_warning) {
            newWarnings[`${type}-${i}`] = r.category_warning;
          }
        });
      });

      setOutcomeEscoMappings(newMappings);
      setOutcomeCategoryWarnings(newWarnings);
      setHasUnsavedChanges(true);
      const warnCount = Object.keys(newWarnings).length;
      if (warnCount > 0) {
        toast.showSuccess(`KI fant ESCO-ferdigheter for ${newMappings.length} læringsutbytter. ${warnCount} mulig feilkategorisering(er) flagget.`);
      } else {
        toast.showSuccess(`KI fant ESCO-ferdigheter for ${newMappings.length} læringsutbytter.`);
      }
    } catch (err: any) {
      toast.showError('Kunne ikke hente ESCO-forslag: ' + (err.message || 'ukjent feil'));
    } finally {
      setAutoSuggestingEscoForOutcomes(false);
    }
  };

  // ESCO Skills functions
  const searchEscoSkills = async (query: string) => {
    if (!query.trim()) {
      setEscoSearchResults([]);
      return;
    }

    setEscoSearching(true);
    try {
      // Task #364 (2026-08-20): bruker lokal ESCO-database (14 671 skills) istedenfor
      // EU-ESCO-API som er ustabilt og ikke støtter norske søkeord. Endpoint
      // returnerer felt "skills" (ikke "results") og forventer language=no.
      const response = await authenticatedFetch(
        `/api/esco/local/skills/search?q=${encodeURIComponent(query)}&language=no&limit=15`
      );
      const data = await response.json();

      if (data.success) {
        const normalizedResults = (data.skills || []).map((skill: any) => {
          const toString = (val: any): string => {
            if (typeof val === 'string') return val;
            if (typeof val === 'object' && val !== null) {
              return val.no || val.en || Object.values(val)[0] || '';
            }
            return String(val || '');
          };
          return {
            uri: toString(skill.uri),
            code: toString(skill.code),
            title: toString(skill.title_no) || toString(skill.title) || toString(skill.title_en) || 'Ukjent',
            description: toString(skill.description_no) || toString(skill.description) || toString(skill.description_en) || '',
            skillType: toString(skill.skill_type) || toString(skill.skillType) || 'skill',
          };
        });
        setEscoSearchResults(normalizedResults);
      } else {
        setError('ESCO søk feilet: ' + (data.error || 'Ukjent feil'));
      }
    } catch (err: any) {
      setError('ESCO søk feilet: ' + err.message);
    } finally {
      setEscoSearching(false);
    }
  };

  const addEscoSkill = (skill: any) => {
    // Check if already selected
    if (selectedEscoSkills.some(s => s.uri === skill.uri)) {
      return;
    }
    setSelectedEscoSkills([...selectedEscoSkills, skill]);
    setEscoSearchQuery('');
    setEscoSearchResults([]);
  };

  const removeEscoSkill = (uri: string) => {
    setSelectedEscoSkills(selectedEscoSkills.filter(s => s.uri !== uri));
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (escoSearchQuery) {
        searchEscoSkills(escoSearchQuery);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [escoSearchQuery]);

  // Save selected ESCO skills to formData
  // VIKTIG: Send alltid array (selv om tom) for å tillate sletting
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      esco_skills: selectedEscoSkills  // Alltid array, tillater tom array for sletting
    }));
  }, [selectedEscoSkills]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Autosave draft (task #300): tilby gjenoppretting hvis vi fant et
          lokalt utkast som er nyere enn siste lagrede versjon i DB */}
      {pendingDraft && (
        <RecoverDraftModal
          draftTimestamp={pendingDraft.timestamp}
          dbUpdatedAt={pendingDraft.meta?.dbUpdatedAt ?? null}
          onRecover={handleRecoverDraft}
          onDiscard={handleDiscardDraft}
        />
      )}

      {/* Konflikt-modal (task #301): vises ved 409 fra backend */}
      {conflictInfo && (
        <ConflictResolutionModal
          currentUpdatedAt={conflictInfo.currentUpdatedAt}
          expectedUpdatedAt={conflictInfo.expectedUpdatedAt}
          lastEditorName={conflictInfo.lastEditorName}
          onOverwrite={handleConflictOverwrite}
          onReload={handleConflictReload}
          comparisonUrl={comparisonUrl}
          onCancel={handleConflictCancel}
        />
      )}

      {/* KI-validering: overlay-modal som låser interaksjon mens KI arbeider */}
      <ValidationProgressModal
        visible={validating}
        startTime={validationStartTime || undefined}
      />

      {/* KI-forbedringsforslag: overlay-modal som låser interaksjon */}
      <ImprovementProgressModal
        visible={loadingImprovements}
        startTime={improvementStartTime || undefined}
      />

      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{id ? 'Rediger' : 'Opprett'} studieplan</h1>
          <div className="flex items-center gap-2">
            {id && <EnglishPreviewButton studyPlanId={id} />}
            {!id && (
              <button
                onClick={() => setShowGenerateModal(true)}
                className="btn btn-secondary flex items-center gap-2 bg-kf-accent hover:bg-kf-accent text-white"
              >
                <Wand2 size={18} />
                KI-generer studieplan
              </button>
            )}
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-6">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-green-600" size={20} />
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      {/* Warning when revalidation needed - Version info box */}
      {/* Debug: needsRevalidation={needsRevalidation}, studyPlanStatus={studyPlanStatus} */}
      {needsRevalidation && studyPlanStatus === 'approved' && (
        <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-kf-blue-100 rounded-full flex items-center justify-center">
              <Layers className="text-kf-blue-600" size={20} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-kf-blue-900 mb-1">
                Du redigerer en godkjent studieplan
              </h4>
              <p className="text-sm text-kf-blue-800 mb-3">
                Endringene dine vil opprette en <strong>ny versjon</strong> av studieplanen når den godkjennes på nytt.
                Den eksisterende versjonen forblir tilgjengelig via sin verifiseringslenke.
              </p>
              <div className="bg-white border border-kf-blue-200 rounded p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-gray-700">Tidligere versjoner bevares og er fortsatt gyldige</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-gray-700">Hver versjon har sin egen verifiseringslenke</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Ny KI-validering kreves før godkjenning</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning for non-approved plans needing revalidation */}
      {needsRevalidation && studyPlanStatus !== 'approved' && (
        <div className="bg-kf-accent-20 border border-kf-accent-40 text-kf-accent px-4 py-3 rounded mb-6">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium">Endringer krever ny KI-validering</p>
              <p className="text-sm mt-1">
                Du har gjort endringer på denne studieplanen. Kjør KI-validering for å se oppdatert kvalitetspoeng.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Generation Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Wand2 className="text-kf-accent" size={24} />
                  <h2 className="text-2xl font-bold">KI-generer studieplan</h2>
                </div>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              {!generating && (
                <p className="text-gray-600 mb-6">
                  Beskriv studieplanen du ønsker, så genererer KI et komplett utkast basert på
                  systemadmins regler for kvalitetssikring.
                </p>
              )}

              {error && !generating && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              {/* Fase 2C v2 (task #287): under generering vises kun fremdriftsindikator */}
              {generating && (
                <div className="py-4">
                  <GenerationProgress
                    phases={generationPhases}
                    isRunning={generating}
                    estimatedHours={typeof generateHours === 'number' ? generateHours : undefined}
                    // Grovt anslag av antall rå utbytter i beskrivelsen basert
                    // på linjeskift, punkter og talløst-format. Pre-analysen
                    // korrigerer dette senere med faktisk telling.
                    inputOutcomesCount={(() => {
                      const desc = generateDescription || '';
                      if (desc.length < 100) return undefined;
                      const bulletMatches = desc.match(/(?:^|\n)\s*[-•*]|(?:^|\n)\s*\d+\.\s/gm);
                      if (bulletMatches && bulletMatches.length >= 3) return bulletMatches.length;
                      // Fallback: 1 utbytte per 200 tegn i lang beskrivelse
                      return Math.min(30, Math.floor(desc.length / 200));
                    })()}
                  />
                </div>
              )}

              <div className={`space-y-4 ${generating ? 'hidden' : ''}`}>
                <div>
                  <label className="label">Beskrivelse av studieplan (påkrevd) *</label>
                  <textarea
                    className="input"
                    rows={6}
                    value={generateDescription}
                    onChange={(e) => setGenerateDescription(e.target.value)}
                    placeholder="Eksempel: En studieplan om digital markedsføring som dekker sosiale medier, SEO, og innholdsmarkedsføring. Målgruppen er voksne uten forkunnskaper."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Jo mer detaljert, jo bedre blir resultatet.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Foretrukket tittel (valgfri)</label>
                    <input
                      type="text"
                      className="input"
                      value={generateTitle}
                      onChange={(e) => setGenerateTitle(e.target.value)}
                      placeholder="Digital Markedsføring"
                    />
                  </div>

                  <div>
                    <label className="label">Estimerte timer *</label>
                    <input
                      type="number"
                      className="input"
                      min="4"
                      value={generateHours}
                      onChange={(e) => setGenerateHours(e.target.value === '' ? '' : parseInt(e.target.value))}
                      placeholder="Minimum 4 timer"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Påvirker omfang og kompleksitet
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label flex items-center gap-1">EQF-nivå <HelpTooltip slug="eqf-nkr-level" size="sm" /></label>
                    <select
                      className="input"
                      value={generateEqfLevel}
                      onChange={(e) => setGenerateEqfLevel(e.target.value)}
                    >
                      <option value="3">Nivå 3 - Grunnleggende</option>
                      <option value="4">Nivå 4 - Videregående</option>
                      <option value="5">Nivå 5 - Fagskole</option>
                      <option value="6">Nivå 6 - Bachelor</option>
                      <option value="7">Nivå 7 - Master</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Kompetansepoeng (valgfri)</label>
                    <input
                      type="number"
                      className="input"
                      min="0"
                      step="0.5"
                      value={generateECTS}
                      onChange={(e) => setGenerateECTS(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      placeholder="Beregnes automatisk"
                    />
                  </div>
                </div>

                <div>
                  <label className="label flex items-center gap-1">Målgruppe (valgfri) <HelpTooltip slug="target-group" size="sm" /></label>
                  <input
                    type="text"
                    className="input"
                    value={generateTargetGroup}
                    onChange={(e) => setGenerateTargetGroup(e.target.value)}
                    placeholder="F.eks. 'Ansatte i helsesektoren'"
                  />
                </div>

                <div>
                  <label className="label flex items-center gap-1">Opptakskrav (valgfri) <HelpTooltip slug="admission-requirements" size="sm" /></label>
                  <input
                    type="text"
                    className="input"
                    value={generateAdmissionReq}
                    onChange={(e) => setGenerateAdmissionReq(e.target.value)}
                    placeholder="F.eks. 'Ingen formelle krav' eller 'Grunnleggende datakunnskaper'"
                  />
                </div>

                <div className="bg-kf-light border border-kf-primary-40 rounded p-4">
                  <p className="text-sm text-kf-primary">
                    <strong>Tips:</strong> KI vil generere læringsutbytte, innholdsmoduler,
                    vurderingskriterier og metadata basert på din beskrivelse. Du kan
                    redigere alt etterpå.
                  </p>
                </div>
              </div>

              {/* Knappene vises både under og etter generering — knapper-rad utenfor form-content */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleGenerate}
                  disabled={generating || !generateDescription.trim()}
                  className="btn btn-primary flex items-center gap-2 flex-1"
                >
                  {generating ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Genererer…
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Generer studieplan
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="btn btn-secondary"
                  disabled={generating}
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fase 1a (task #271): pre-analyse-info etter KI-generering */}
      {preAnalysisResult && (
        <div className="border border-kf-blue-200 bg-kf-blue-50 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-kf-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-kf-blue-900">KI-analyse av din input</p>
              <p className="text-sm text-kf-blue-800 mt-1">
                {preAnalysisResult.strategy === 'bevar' && (
                  <>Vi bevarte dine detaljerte læringsutbytter
                  ({preAnalysisResult.existing_outcomes?.approximate_count || 0} læringsutbytter identifisert i input)
                  og beholdt {preAnalysisResult.domain_terms?.length || 0} fagspesifikke termer.</>
                )}
                {preAnalysisResult.strategy === 'forbedre' && (
                  <>Vi forbedret strukturen og språket i din input,
                  mens vi beholdt fagspesifikke termer som var spesifisert.</>
                )}
                {preAnalysisResult.strategy === 'generer_fra_null' && (
                  <>Vi bygde studieplanen ut fra din beskrivelse.</>
                )}
              </p>

              {/* Bevarte fagord — kollapsbar */}
              {preAnalysisResult.domain_terms?.length > 0 && (
                <details className="mt-2 text-xs text-kf-blue-700">
                  <summary className="cursor-pointer hover:underline">
                    Identifiserte fagord ({preAnalysisResult.domain_terms.length})
                  </summary>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {preAnalysisResult.domain_terms.map((t: string) => (
                      <span key={t} className="px-2 py-0.5 bg-kf-blue-100 rounded text-kf-blue-800">{t}</span>
                    ))}
                  </div>
                </details>
              )}

              {/* Time-anslag-advarsel hvis stort avvik */}
              {preAnalysisResult.hours_estimate?.hours_mismatch === 'for_lavt' && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                  <strong>Time-anslag:</strong> Du har satt {preAnalysisResult.hours_estimate?.user_provided_hours} timer,
                  men innholdet beskriver et kurs som realistisk krever
                  {' '}{preAnalysisResult.hours_estimate.min}-{preAnalysisResult.hours_estimate.max} timer.
                  <br />
                  <em className="text-amber-800">{preAnalysisResult.hours_estimate.basis}</em>
                </div>
              )}
              {preAnalysisResult.hours_estimate?.hours_mismatch === 'for_høyt' && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                  <strong>Time-anslag:</strong> Du har satt {preAnalysisResult.hours_estimate?.user_provided_hours} timer,
                  men innholdet beskriver et mindre omfattende kurs (anslag
                  {' '}{preAnalysisResult.hours_estimate.min}-{preAnalysisResult.hours_estimate.max} timer).
                  <br />
                  <em className="text-amber-800">{preAnalysisResult.hours_estimate.basis}</em>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fase 2A/2B (tasks #273/274): komprimering + kompleksitetsvurdering */}
      {(compressionResult || complexityResult) && (
        <div className="mb-6">
          <CompressionDisplay
            compression={compressionResult}
            complexityCheck={complexityResult}
          />
        </div>
      )}

      {validationResult && validationResult.score !== undefined && (
        <div className={`border rounded-lg p-6 mb-6 ${
          validationResult.decision === 'approved' ? 'bg-green-50 border-green-200' :
          validationResult.decision === 'rejected_with_revisions' ? 'bg-yellow-50 border-yellow-300' :
          'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Valideringsresultat</h3>
            <div className="flex items-center gap-2">
              {validationResult.decision === 'approved' && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center gap-1">
                  <CheckCircle size={16} /> Godkjent
                </span>
              )}
              {validationResult.decision === 'rejected_with_revisions' && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium flex items-center gap-1">
                  <AlertTriangle size={16} /> Krever revisjon
                </span>
              )}
              {validationResult.decision === 'rejected' && (
                <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium flex items-center gap-1">
                  <X size={16} /> Avvist
                </span>
              )}
            </div>
          </div>

          {/* Score and Summary */}
          <div className="flex items-center gap-4 mb-4">
            <div className={`text-3xl font-bold ${
              validationResult.score >= 80 ? 'text-green-600' :
              validationResult.score >= 60 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {Number(validationResult.score).toFixed(0)}%
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700">{validationResult.feedback?.summary || 'Ingen tilbakemelding'}</p>
            </div>
          </div>

          {/* Scope Classification */}
          {validationResult.feedback?.scope_classification && (
            <div className="bg-white bg-opacity-50 rounded p-3 mb-4">
              <p className="text-sm">
                <span className="font-medium">Omfang:</span> {' '}
                {validationResult.feedback.scope_classification.category === 'micro' && 'Micro (4–15 timer)'}
                {validationResult.feedback.scope_classification.category === 'small' && 'Small (16–49 timer)'}
                {validationResult.feedback.scope_classification.category === 'medium' && 'Medium (50–149 timer)'}
                {validationResult.feedback.scope_classification.category === 'extensive' && 'Extensive (150+ timer)'}
                {typeof validationResult.feedback.scope_classification.hours === 'number' &&
                  validationResult.feedback.scope_classification.hours > 0 && (
                  <> — denne studieplanen: <strong>{validationResult.feedback.scope_classification.hours} timer</strong></>
                )}
              </p>
            </div>
          )}

          {/* Norsk språkkvalitet */}
          {validationResult.feedback?.norwegian_language_quality && (
            <div className={`rounded p-3 mb-4 border ${
              validationResult.feedback.norwegian_language_quality.score >= 7
                ? 'bg-green-50 border-green-200'
                : 'bg-yellow-50 border-yellow-300'
            }`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">Norsk språkkvalitet</span>
                <span className={`text-sm font-bold ${
                  validationResult.feedback.norwegian_language_quality.score >= 7
                    ? 'text-green-700' : 'text-yellow-700'
                }`}>
                  {validationResult.feedback.norwegian_language_quality.score}/10
                </span>
              </div>
              {validationResult.feedback.norwegian_language_quality.feedback && (
                <p className="text-xs text-gray-700 mb-1">
                  {validationResult.feedback.norwegian_language_quality.feedback}
                </p>
              )}
              {validationResult.feedback.norwegian_language_quality.issues?.length > 0 && (
                <ul className="text-xs text-yellow-800 list-disc list-inside mt-1">
                  {validationResult.feedback.norwegian_language_quality.issues.map((issue, idx) => (
                    <li key={idx}>{issue}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Scoring Categories Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            {validationResult.feedback?.learning_outcomes_quality && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Læringsutbytter</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.learning_outcomes_quality.score >= 20 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.learning_outcomes_quality.score}/25
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.learning_outcomes_quality.score / 25) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.constructive_alignment && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Konstruktiv alignment</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.constructive_alignment.score >= 12 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.constructive_alignment.score}/15
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.constructive_alignment.score / 15) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.workload_realism && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Arbeidsbelastning</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.workload_realism.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.workload_realism.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.workload_realism.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.structure_quality && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Struktur</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.structure_quality.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.structure_quality.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.structure_quality.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.microcredential_fitness && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Microcredential</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.microcredential_fitness.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.microcredential_fitness.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.microcredential_fitness.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.metadata_elm_compliance && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Metadata/ELM</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.metadata_elm_compliance.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.metadata_elm_compliance.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.metadata_elm_compliance.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.practical_skills && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Praktiske ferdigheter</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.practical_skills.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.practical_skills.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.practical_skills.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.soft_skills && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Myke ferdigheter</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.soft_skills.score >= 4 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.soft_skills.score}/5
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.soft_skills.score / 5) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.narrative_readiness && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Attesttekst-klar</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.narrative_readiness.score >= 4 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.narrative_readiness.score}/5
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.narrative_readiness.score / 5) * 100}%`}}></div>
                </div>
              </div>
            )}
          </div>

          {/* NKR/EQF Qualification Framework Alignment */}
          {validationResult.feedback?.qualification_framework_alignment && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-medium mb-3 flex items-center gap-2 text-purple-800">
                <GraduationCap size={16} className="text-purple-600" />
                NKR/EQF Nivåvurdering
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Angitt nivå:</span>
                  <span className="font-medium text-purple-800">
                    EQF/NKR {validationResult.feedback.qualification_framework_alignment.claimed_level}
                  </span>
                </div>

                {!validationResult.feedback.qualification_framework_alignment.level_appropriate && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-yellow-800 text-sm">
                      <strong>Anbefalt nivå:</strong> EQF/NKR {validationResult.feedback.qualification_framework_alignment.recommended_level}
                    </p>
                  </div>
                )}

                {validationResult.feedback.qualification_framework_alignment.level_appropriate && (
                  <div className="bg-white border border-purple-200 rounded-md p-3">
                    <p className="text-purple-700 text-sm flex items-center gap-2">
                      <CheckCircle size={14} />
                      Nivåplassering er passende
                    </p>
                  </div>
                )}

                <p className="text-sm text-gray-600">
                  {validationResult.feedback.qualification_framework_alignment.feedback}
                </p>

                {/* Detailed alignment per category */}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {validationResult.feedback.qualification_framework_alignment.knowledge_alignment && (
                    <div className={`p-2 rounded text-xs border ${validationResult.feedback.qualification_framework_alignment.knowledge_alignment.appropriate ? 'bg-white border-purple-200 text-purple-700' : 'bg-yellow-100 border-yellow-200 text-yellow-800'}`}>
                      <div className="font-medium">Kunnskap</div>
                      <div className="truncate">{validationResult.feedback.qualification_framework_alignment.knowledge_alignment.appropriate ? 'Passende' : 'Avvik'}</div>
                    </div>
                  )}
                  {validationResult.feedback.qualification_framework_alignment.skills_alignment && (
                    <div className={`p-2 rounded text-xs border ${validationResult.feedback.qualification_framework_alignment.skills_alignment.appropriate ? 'bg-white border-purple-200 text-purple-700' : 'bg-yellow-100 border-yellow-200 text-yellow-800'}`}>
                      <div className="font-medium">Ferdigheter</div>
                      <div className="truncate">{validationResult.feedback.qualification_framework_alignment.skills_alignment.appropriate ? 'Passende' : 'Avvik'}</div>
                    </div>
                  )}
                  {validationResult.feedback.qualification_framework_alignment.competence_alignment && (
                    <div className={`p-2 rounded text-xs border ${validationResult.feedback.qualification_framework_alignment.competence_alignment.appropriate ? 'bg-white border-purple-200 text-purple-700' : 'bg-yellow-100 border-yellow-200 text-yellow-800'}`}>
                      <div className="font-medium">Kompetanse</div>
                      <div className="truncate">{validationResult.feedback.qualification_framework_alignment.competence_alignment.appropriate ? 'Passende' : 'Avvik'}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ESCO Skills Relevance */}
          {validationResult.feedback?.esco_relevance && (
            <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-teal-800">
                <Tag size={16} className="text-teal-600" />
                ESCO-ferdigheter
              </h4>
              <div className="flex items-center gap-4 mb-2">
                <span className="text-sm text-gray-700">
                  {validationResult.feedback.esco_relevance.relevant_skills} av {validationResult.feedback.esco_relevance.total_skills} ESCO-koblinger er relevante
                </span>
                <span className={`text-sm font-medium ${validationResult.feedback.esco_relevance.score >= 70 ? 'text-green-600' : 'text-yellow-600'}`}>
                  {validationResult.feedback.esco_relevance.score}%
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {validationResult.feedback.esco_relevance.feedback}
              </p>
            </div>
          )}

          {/* Translation Quality - Oversettelseskvalitet */}
          {validationResult.feedback?.translation_quality && (
            <div className="mt-4 bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-kf-blue-800">
                <Languages size={16} className="text-kf-blue-600" />
                Oversettelseskvalitet (Engelsk)
              </h4>
              <div className="flex items-center gap-4 mb-3">
                <span className="text-sm text-gray-700">Kvalitetsscore:</span>
                <span className={`text-xl font-bold ${
                  validationResult.feedback.translation_quality.score >= 80
                    ? 'text-green-600'
                    : validationResult.feedback.translation_quality.score >= 60
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`}>
                  {validationResult.feedback.translation_quality.score}%
                </span>
              </div>

              {validationResult.feedback.translation_quality.issues && validationResult.feedback.translation_quality.issues.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-red-700 mb-1">Problemer funnet:</p>
                  <ul className="list-disc pl-5 text-sm text-red-600 space-y-1">
                    {validationResult.feedback.translation_quality.issues.map((issue: string, i: number) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-sm text-gray-600">
                {validationResult.feedback.translation_quality.feedback}
              </p>

              {validationResult.feedback.translation_quality.recommendations && validationResult.feedback.translation_quality.recommendations.length > 0 && (
                <div className="mt-3 pt-3 border-t border-kf-blue-200">
                  <p className="text-sm font-medium text-kf-blue-700 mb-1">Anbefalinger:</p>
                  <ul className="list-disc pl-5 text-sm text-kf-blue-600 space-y-1">
                    {validationResult.feedback.translation_quality.recommendations.map((rec: string, i: number) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Bloom Profile Analysis - Holistisk vurdering */}
          {validationResult.feedback?.bloom_profile_analysis && (
            <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-indigo-800">
                <BookOpen size={16} className="text-indigo-600" />
                Bloom-profilanalyse
              </h4>

              <p className="text-sm text-gray-700 mb-3">
                {validationResult.feedback.bloom_profile_analysis.summary}
              </p>

              {/* Kategori-profiler */}
              <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                {validationResult.feedback.bloom_profile_analysis.per_category_profile &&
                  Object.entries(validationResult.feedback.bloom_profile_analysis.per_category_profile).map(([cat, data]: [string, any]) => (
                  <div key={cat} className={`p-2 rounded ${
                    data.assessment === 'appropriate' || data.assessment === 'strong'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <span className="font-medium block">
                      {cat === 'knowledge' ? 'Kunnskap' : cat === 'skills' ? 'Ferdigheter' : 'Kompetanse'}
                    </span>
                    <div className="text-gray-600 mt-1">
                      Bloom {data.lowest_level}-{data.highest_level}
                    </div>
                    <div className="text-gray-500 mt-1 text-[10px]">{data.note}</div>
                  </div>
                ))}
              </div>

              {/* Profilbalanse */}
              {validationResult.feedback.bloom_profile_analysis.profile_balance && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    validationResult.feedback.bloom_profile_analysis.profile_balance.has_foundation
                      ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {validationResult.feedback.bloom_profile_analysis.profile_balance.has_foundation
                      ? '✓ Fundament (Bloom 2-3)' : '⚠ Mangler fundament'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    validationResult.feedback.bloom_profile_analysis.profile_balance.has_complexity
                      ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {validationResult.feedback.bloom_profile_analysis.profile_balance.has_complexity
                      ? '✓ Kompleksitet (Bloom 4-5)' : '⚠ Mangler kompleksitet'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    validationResult.feedback.bloom_profile_analysis.profile_balance.has_progression
                      ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {validationResult.feedback.bloom_profile_analysis.profile_balance.has_progression
                      ? '✓ Progresjon' : '⚠ Mangler progresjon'}
                  </span>
                </div>
              )}

              {/* Anbefalinger */}
              {validationResult.feedback.bloom_profile_analysis.recommendations &&
               validationResult.feedback.bloom_profile_analysis.recommendations.length > 0 && (
                <div className="mt-2 p-2 bg-amber-100 rounded text-sm">
                  <strong className="text-amber-800">Anbefalinger:</strong>
                  <ul className="list-disc ml-4 mt-1 text-amber-900">
                    {validationResult.feedback.bloom_profile_analysis.recommendations.map((rec: any, i: number) => (
                      <li key={i}>{rec.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-[10px] text-gray-500 mt-2 italic">
                {validationResult.feedback.bloom_profile_analysis.bloom_as_support_tool_note}
              </p>
            </div>
          )}

          {/* Flagged Outcomes with Suggestions */}
          {validationResult.feedback?.flagged_outcomes && validationResult.feedback.flagged_outcomes.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <AlertTriangle size={16} className="text-yellow-600" />
                Læringsutbytter som kan forbedres
              </h4>
              <div className="space-y-3">
                {validationResult.feedback.flagged_outcomes.map((flagged: any, idx: number) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                        {flagged.type === 'knowledge' ? 'Kunnskap' : flagged.type === 'skills' ? 'Ferdighet' : 'Kompetanse'} #{flagged.index + 1}
                      </span>
                      <span className="text-xs text-red-600">{flagged.issue}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-red-50 rounded border border-red-100">
                        <p className="text-xs font-medium text-red-700 mb-1">Nåværende:</p>
                        <p className="text-sm text-gray-700">{flagged.original}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded border border-green-100">
                        <p className="text-xs font-medium text-green-700 mb-1">Forslag:</p>
                        <p className="text-sm text-gray-700">{flagged.suggested}</p>
                        <button
                          type="button"
                          onClick={() => {
                            // Deep copy to avoid mutating originalFormData - preserve exact structure
                            const outcomes = JSON.parse(JSON.stringify(formData.learning_outcomes || {}));
                            const typeKey = flagged.type as 'knowledge' | 'skills' | 'competence';
                            if (outcomes[typeKey] && outcomes[typeKey][flagged.index]) {
                              outcomes[typeKey][flagged.index] = flagged.suggested;
                              setFormData({...formData, learning_outcomes: outcomes});
                              setHasUnsavedChanges(true);
                              setNeedsRevalidation(true);
                            }
                          }}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Improvement Suggestions */}
          {validationResult.feedback?.improvement_suggestions && validationResult.feedback.improvement_suggestions.length > 0 && (
            <div className="mt-4 bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-kf-blue-800">
                <Info size={16} className="text-kf-blue-600" />
                Forbedringsforslag
              </h4>
              <ul className="list-disc list-inside space-y-1">
                {validationResult.feedback.improvement_suggestions.map((suggestion: string, idx: number) => (
                  <li key={idx} className="text-sm text-kf-blue-900">{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {/* KI-forslag til justeringer - detailed per-field improvements */}
          <div className="mt-4 border-t pt-4">
            {/* Task #333 (2026-07-03): knappen er ikke lenger disabled ved ulagrede
                endringer. handleFetchImprovements viser confirm-dialog + auto-lagrer
                før den kjører KI-forslag. Brukeren slipper å scrolle helt ned for å
                lagre manuelt. */}
            <button
              type="button"
              onClick={handleFetchImprovements}
              disabled={loadingImprovements}
              className="btn flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60"
              title={hasUnsavedChanges ? 'Vil lagre endringene før KI-forslag' : ''}
            >
              {loadingImprovements ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Henter KI-forslag...
                </>
              ) : (
                <>
                  <Lightbulb size={18} />
                  {hasUnsavedChanges ? 'Lagre og hent KI-forslag' : 'Vis KI-forslag til justeringer'}
                </>
              )}
            </button>

            {/* Collapsible improvements section */}
            {improvements && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowImprovements(!showImprovements)}
                  className="flex items-center gap-2 text-amber-700 hover:text-amber-900 font-medium"
                >
                  {showImprovements ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  {showImprovements ? 'Skjul forslag' : 'Vis forslag'}
                </button>

                {showImprovements && (
                  <div className="mt-3 space-y-4">
                    {/* Summary */}
                    {improvements.summary && (
                      <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                        {improvements.summary}
                      </div>
                    )}

                    {/* Profile Assessment - Expected effect of accepting suggestions */}
                    {improvements.profile_assessment && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <strong className="text-green-800">Forventet effekt av forslag:</strong>
                        <p className="mt-1 text-gray-700">{improvements.profile_assessment.explanation}</p>
                        <div className="flex gap-4 mt-2 text-xs">
                          <span className="text-gray-600">
                            Nå: <span className="font-medium px-1.5 py-0.5 bg-amber-100 rounded">
                              {improvements.profile_assessment.before === 'balanced' ? 'Balansert' :
                               improvements.profile_assessment.before === 'needs_foundation' ? 'Mangler fundament' :
                               improvements.profile_assessment.before === 'needs_complexity' ? 'Mangler kompleksitet' :
                               improvements.profile_assessment.before === 'needs_adjustment' ? 'Behov for justering' :
                               improvements.profile_assessment.before}
                            </span>
                          </span>
                          <span>→</span>
                          <span className="text-gray-600">
                            Etter: <span className="font-medium text-green-700 px-1.5 py-0.5 bg-green-100 rounded">
                              {improvements.profile_assessment.after_if_accepted === 'balanced' ? 'Balansert' :
                               improvements.profile_assessment.after_if_accepted === 'needs_foundation' ? 'Mangler fundament' :
                               improvements.profile_assessment.after_if_accepted === 'needs_complexity' ? 'Mangler kompleksitet' :
                               improvements.profile_assessment.after_if_accepted === 'needs_adjustment' ? 'Behov for justering' :
                               improvements.profile_assessment.after_if_accepted}
                            </span>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Kategoriforslag (v5.14) — basert på yrkesroller */}
                    {Array.isArray((improvements as any).suggested_categories) && (improvements as any).suggested_categories.length > 0 && (
                      <div className="p-3 bg-white border border-purple-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                          <Zap size={14} className="text-purple-600" />
                          Foreslåtte kategorier for kompetanseheving.no
                        </h5>
                        <p className="text-xs text-gray-500 mb-3">
                          Basert på yrkesrollene som er knyttet til studieplanen. Klikk for å legge til.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(improvements as any).suggested_categories
                            .filter((s: { category: string }) => !(formData.categories ?? []).includes(s.category))
                            .map((s: { category: string; score: number; matched: string[] }) => (
                              <button
                                key={s.category}
                                type="button"
                                onClick={() => {
                                  const newCats = [...(formData.categories ?? []), s.category];
                                  setFormData({ ...formData, categories: newCats });
                                  setHasUnsavedChanges(true);
                                }}
                                className="text-xs px-2.5 py-1 bg-purple-50 hover:bg-purple-100 border border-purple-300 text-purple-900 rounded-full inline-flex items-center gap-1"
                                title={s.matched && s.matched.length > 0 ? `Foreslått fra: ${s.matched.join(', ')}` : undefined}
                              >
                                + {s.category}
                              </button>
                            ))}
                          {(improvements as any).suggested_categories.every((s: { category: string }) => (formData.categories ?? []).includes(s.category)) && (
                            <span className="text-xs text-purple-700 italic">Alle forslag er allerede valgt</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ESCO Skill Suggestions for Learning Outcomes */}
                    {improvements.suggested_outcome_skills && Object.keys(improvements.suggested_outcome_skills).length > 0 && (() => {
                      // Task #335 (2026-07-03): for micro-kurs (< 15 timer) skjul
                      // svake ESCO-forslag (< 60%) fordi ESCO er profesjons-
                      // fokusert og gir villedende matches for allmenn-tema.
                      const isMicro = (formData.estimated_hours || 0) < 15;
                      const microThreshold = 0.60;
                      const filteredEntries = Object.entries(improvements.suggested_outcome_skills).map(([key, suggestion]: [string, any]) => {
                        if (isMicro) {
                          const filtered = (suggestion.suggestedSkills || []).filter((s: any) => (s.score || 0) >= microThreshold);
                          return [key, { ...suggestion, suggestedSkills: filtered }] as [string, any];
                        }
                        return [key, suggestion] as [string, any];
                      });
                      const totalRemaining = filteredEntries.reduce((sum, [, s]) => sum + (s.suggestedSkills?.length || 0), 0);
                      // Hvis micro-kurs og alle forslag ble filtrert bort → vis info-boks istedet
                      if (isMicro && totalRemaining === 0) {
                        return (
                          <div className="p-3 bg-kf-blue-50 border border-kf-blue-200 rounded-lg">
                            <h5 className="font-medium text-sm mb-2 flex items-center gap-1.5 text-kf-blue-900">
                              <Info size={14} className="text-kf-blue-600" />
                              Ingen sterke ESCO-koblinger funnet
                            </h5>
                            <p className="text-xs text-gray-700">
                              For korte kurs på allmenn-nivå (livskompetanse, privatøkonomi, hobby o.l.) har ESCO ofte ikke passende ferdighetskoder.
                              ESCO er utviklet for profesjons- og arbeidsmarked-relaterte ferdigheter.
                              Bruk gjerne <em>«Legg til ESCO-ferdighet»</em> under hvert læringsutbytte for å søke opp koblinger manuelt hvis du finner passende koder.
                            </p>
                          </div>
                        );
                      }
                      return (
                      <div className="p-3 bg-white border border-purple-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                          <Zap size={14} className="text-purple-600" />
                          Foreslåtte ESCO-ferdigheter
                        </h5>
                        <p className="text-xs text-gray-500 mb-3">
                          For læringsutbytter uten ESCO-kobling. Klikk for å legge til.
                          {isMicro && <span className="ml-1 italic">(Kun sterke matches vises for korte kurs.)</span>}
                        </p>
                        <div className="space-y-3">
                          {filteredEntries.filter(([, s]) => s.suggestedSkills?.length > 0).map(([key, suggestion]: [string, any]) => {
                            const categoryLabels: Record<string, string> = {
                              knowledge: 'Kunnskap',
                              skills: 'Ferdigheter',
                              competence: 'Kompetanse'
                            };
                            return (
                              <div key={key} className="p-2 bg-purple-50 rounded border border-purple-100">
                                <div className="text-xs text-purple-700 mb-1">
                                  {categoryLabels[suggestion.outcomeType] || suggestion.outcomeType} #{suggestion.outcomeIndex + 1}:
                                </div>
                                <div className="text-sm text-gray-700 mb-2 italic">
                                  "{suggestion.outcomeText}"
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {suggestion.suggestedSkills.map((skill: any) => {
                                    // Check if already added to outcomeEscoMappings
                                    const existingMapping = outcomeEscoMappings.find(
                                      (m: OutcomeESCOMapping) => m.outcomeType === suggestion.outcomeType && m.outcomeIndex === suggestion.outcomeIndex
                                    );
                                    const isAlreadyAdded = existingMapping?.escoSkills?.some((s: ESCOSkillMatch) => s.uri === skill.uri);

                                    return (
                                      <button
                                        key={skill.uri}
                                        type="button"
                                        disabled={isAlreadyAdded}
                                        onClick={() => {
                                          if (!isAlreadyAdded) {
                                            // Add skill to the mapping for this outcome
                                            const newSkill: ESCOSkillMatch = {
                                              uri: skill.uri,
                                              code: skill.code,
                                              title: skill.title,
                                              description: skill.description,
                                              skillType: skill.skillType || 'skill',
                                              score: skill.score,
                                              confirmed: false
                                            };

                                            setOutcomeEscoMappings((prev: OutcomeESCOMapping[]) => {
                                              const existing = prev.find(
                                                (m: OutcomeESCOMapping) => m.outcomeType === suggestion.outcomeType && m.outcomeIndex === suggestion.outcomeIndex
                                              );
                                              if (existing) {
                                                // Add to existing mapping
                                                return prev.map((m: OutcomeESCOMapping) =>
                                                  m.outcomeType === suggestion.outcomeType && m.outcomeIndex === suggestion.outcomeIndex
                                                    ? { ...m, escoSkills: [...m.escoSkills, newSkill] }
                                                    : m
                                                );
                                              } else {
                                                // Create new mapping
                                                return [...prev, {
                                                  outcomeIndex: suggestion.outcomeIndex,
                                                  outcomeType: suggestion.outcomeType,
                                                  escoSkills: [newSkill]
                                                }];
                                              }
                                            });
                                            setHasUnsavedChanges(true);
                                            setNeedsRevalidation(true);
                                          }
                                        }}
                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                          isAlreadyAdded
                                            ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                            : 'bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200 cursor-pointer'
                                        }`}
                                        title={skill.description || (isAlreadyAdded ? 'Allerede lagt til' : 'Klikk for å legge til')}
                                      >
                                        <Zap size={10} />
                                        <span className="max-w-48 truncate">{skill.title}</span>
                                        <span className={isAlreadyAdded ? 'text-gray-400' : 'text-purple-500'}>
                                          {Math.round((skill.score || 0.5) * 100)}%
                                        </span>
                                        {!isAlreadyAdded && <Plus size={10} />}
                                        {isAlreadyAdded && <CheckCircle size={10} />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })()}

                    {/* Target Group */}
                    {improvements.target_group?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Målgruppe</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700">{improvements.target_group.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700">{improvements.target_group.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.target_group.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('target_group', improvements.target_group.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Occupation suggestions for target group */}
                    {improvements.target_group?.suggested_occupations?.length > 0 && (
                      <div className="p-3 bg-white border border-indigo-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                          <Briefcase size={14} className="text-indigo-600" />
                          Foreslåtte yrkesroller
                        </h5>
                        <p className="text-xs text-gray-500 mb-3">
                          Basert på studieplanens innhold. Klikk for å legge til.
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {improvements.target_group.suggested_occupations.map((occ: { uri: string; title: string; description?: string; score?: number }) => {
                            const isAlreadyAdded = targetGroupOccupations.some(o => o.uri === occ.uri);
                            return (
                              <button
                                key={occ.uri}
                                type="button"
                                disabled={isAlreadyAdded}
                                onClick={() => {
                                  if (!isAlreadyAdded) {
                                    setTargetGroupOccupations(prev => [...prev, occ as ESCOOccupationMatch]);
                                    setHasUnsavedChanges(true);
                                    setNeedsRevalidation(true);
                                  }
                                }}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                  isAlreadyAdded
                                    ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                    : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 cursor-pointer'
                                }`}
                                title={occ.description || (isAlreadyAdded ? 'Allerede lagt til' : 'Klikk for å legge til')}
                              >
                                <Briefcase size={10} />
                                <span className="max-w-40 truncate">{occ.title}</span>
                                <span className={isAlreadyAdded ? 'text-gray-400' : 'text-indigo-400'}>
                                  {Math.round((occ.score || 0.5) * 100)}%
                                </span>
                                {!isAlreadyAdded && <Plus size={10} />}
                                {isAlreadyAdded && <CheckCircle size={10} />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Prerequisites */}
                    {improvements.admission_requirements?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Opptakskrav</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700">{improvements.admission_requirements.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700">{improvements.admission_requirements.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.admission_requirements.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('admission_requirements', improvements.admission_requirements.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Teaching and Learning Methods */}
                    {improvements.teaching_learning_methods?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Undervisnings- og læringsformer</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700 whitespace-pre-wrap">{improvements.teaching_learning_methods.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700 whitespace-pre-wrap">{improvements.teaching_learning_methods.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.teaching_learning_methods.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('teaching_learning_methods', improvements.teaching_learning_methods.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Description */}
                    {improvements.description?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Beskrivelse</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700 whitespace-pre-wrap">{improvements.description.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700 whitespace-pre-wrap">{improvements.description.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.description.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('description', improvements.description.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Assessment Criteria */}
                    {improvements.assessment_criteria?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Vurderingskriterier</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700 whitespace-pre-wrap">{improvements.assessment_criteria.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700 whitespace-pre-wrap">{improvements.assessment_criteria.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.assessment_criteria.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('assessment_criteria', improvements.assessment_criteria.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Learning Outcomes - Knowledge */}
                    {improvements.learning_outcomes?.knowledge?.some((k: any) => k.needs_improvement) && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Læringsutbytte - Kunnskaper</h5>
                        {improvements.learning_outcomes.knowledge
                          .filter((k: any) => k.needs_improvement)
                          .map((item: any, idx: number) => (
                            <div key={idx} className="mt-2 p-2 bg-gray-50 rounded">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500 text-xs">Nåværende ({item.index + 1}):</span>
                                  <p className="text-gray-700">{item.current}</p>
                                </div>
                                <div>
                                  <span className="text-green-600 text-xs">Forslag:</span>
                                  <p className="text-green-700">{item.suggested}</p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                              <button
                                type="button"
                                onClick={() => applyImprovement('learning_outcomes', item.suggested, 'knowledge', item.index)}
                                className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                              >
                                <CheckCircle size={12} /> Bruk forslag
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Learning Outcomes - Skills */}
                    {improvements.learning_outcomes?.skills?.some((s: any) => s.needs_improvement) && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Læringsutbytte - Ferdigheter</h5>
                        {improvements.learning_outcomes.skills
                          .filter((s: any) => s.needs_improvement)
                          .map((item: any, idx: number) => (
                            <div key={idx} className="mt-2 p-2 bg-gray-50 rounded">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500 text-xs">Nåværende ({item.index + 1}):</span>
                                  <p className="text-gray-700">{item.current}</p>
                                </div>
                                <div>
                                  <span className="text-green-600 text-xs">Forslag:</span>
                                  <p className="text-green-700">{item.suggested}</p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                              <button
                                type="button"
                                onClick={() => applyImprovement('learning_outcomes', item.suggested, 'skills', item.index)}
                                className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                              >
                                <CheckCircle size={12} /> Bruk forslag
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Learning Outcomes - Competence */}
                    {improvements.learning_outcomes?.competence?.some((c: any) => c.needs_improvement) && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Læringsutbytte - Generell kompetanse</h5>
                        {improvements.learning_outcomes.competence
                          .filter((c: any) => c.needs_improvement)
                          .map((item: any, idx: number) => (
                            <div key={idx} className="mt-2 p-2 bg-gray-50 rounded">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500 text-xs">Nåværende ({item.index + 1}):</span>
                                  <p className="text-gray-700">{item.current}</p>
                                </div>
                                <div>
                                  <span className="text-green-600 text-xs">Forslag:</span>
                                  <p className="text-green-700">{item.suggested}</p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                              <button
                                type="button"
                                onClick={() => applyImprovement('learning_outcomes', item.suggested, 'competence', item.index)}
                                className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                              >
                                <CheckCircle size={12} /> Bruk forslag
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Content Modules */}
                    {improvements.content_modules?.some((m: any) => m.needs_improvement) && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Innholdsmoduler</h5>
                        {improvements.content_modules
                          .filter((m: any) => m.needs_improvement)
                          .map((item: any, idx: number) => {
                            // Task #317 (2026-07-02): backend gir noen ganger ikke index
                            // → NaN. Fall til posisjonell nummer. content_modules kan være
                            // strings (ikke {title, description}), samme som task #315.
                            const moduleNumber = Number.isFinite(item.index) ? item.index + 1 : idx + 1;
                            const currentTitle = typeof item.current === 'string'
                              ? item.current
                              : (item.current?.title || '(uten tittel)');
                            const suggestedTitle = typeof item.suggested === 'string'
                              ? item.suggested
                              : (item.suggested?.title || '');
                            return (
                              <div key={idx} className="mt-2 p-2 bg-gray-50 rounded">
                                <div className="text-sm">
                                  <span className="text-gray-500 text-xs">Modul {moduleNumber}:</span>
                                  <div className="grid grid-cols-2 gap-3 mt-1">
                                    <div>
                                      <span className="text-gray-500 text-xs">Nåværende:</span>
                                      <p className="text-gray-700">{currentTitle}</p>
                                    </div>
                                    <div>
                                      <span className="text-green-600 text-xs">Forslag:</span>
                                      <p className="text-green-700">{suggestedTitle}</p>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                                <button
                                  type="button"
                                  onClick={() => applyImprovement('content_modules', item.suggested, undefined, Number.isFinite(item.index) ? item.index : idx)}
                                  className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                                >
                                  <CheckCircle size={12} /> Bruk forslag
                                </button>
                              </div>
                            );
                          })}
                      </div>
                    )}

                    {/* Metadata - EU Standards */}
                    {(improvements.metadata?.eqf_level?.needs_improvement ||
                      improvements.metadata?.esco_skills?.needs_improvement ||
                      improvements.metadata?.isced_code?.needs_improvement ||
                      improvements.metadata?.keywords?.needs_improvement) && (
                      <div className="p-3 bg-white border border-purple-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2 text-purple-800">Metadata (EU-standarder)</h5>

                        {/* EQF Level */}
                        {improvements.metadata?.eqf_level?.needs_improvement && (
                          <div className="mt-2 p-2 bg-purple-50 rounded">
                            <span className="text-purple-600 text-xs font-medium">EQF-nivå</span>
                            <div className="grid grid-cols-2 gap-3 mt-1">
                              <div>
                                <span className="text-gray-500 text-xs">Nåværende:</span>
                                <p className="text-gray-700">{improvements.metadata.eqf_level.current || '(ikke satt)'}</p>
                              </div>
                              <div>
                                <span className="text-green-600 text-xs">Forslag:</span>
                                <p className="text-green-700 font-medium">Nivå {improvements.metadata.eqf_level.suggested}</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{improvements.metadata.eqf_level.reason}</p>
                            <button
                              type="button"
                              onClick={() => applyImprovement('metadata', improvements.metadata.eqf_level.suggested, 'eqf_level')}
                              className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Bruk forslag
                            </button>
                          </div>
                        )}

                        {/* ESCO Skills */}
                        {improvements.metadata?.esco_skills?.needs_improvement && (
                          <div className="mt-2 p-2 bg-purple-50 rounded">
                            <span className="text-purple-600 text-xs font-medium">ESCO-ferdigheter</span>
                            <div className="mt-1">
                              <span className="text-gray-500 text-xs">Nåværende:</span>
                              <p className="text-gray-700 text-sm">
                                {(improvements.metadata.esco_skills.current?.length > 0)
                                  ? improvements.metadata.esco_skills.current.map((s: any) => typeof s === 'string' ? s : s.title || s.name || JSON.stringify(s)).join(', ')
                                  : '(ingen)'}
                              </p>
                            </div>
                            <div className="mt-2">
                              <span className="text-green-600 text-xs">Forslag:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {improvements.metadata.esco_skills.suggested?.map((skill: string, idx: number) => (
                                  <span key={idx} className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">{improvements.metadata.esco_skills.reason}</p>
                            <button
                              type="button"
                              onClick={() => applyImprovement('metadata', improvements.metadata.esco_skills.suggested, 'esco_skills')}
                              className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Bruk forslag
                            </button>
                          </div>
                        )}

                        {/* ISCED Code */}
                        {improvements.metadata?.isced_code?.needs_improvement && (
                          <div className="mt-2 p-2 bg-purple-50 rounded">
                            <span className="text-purple-600 text-xs font-medium">ISCED-F 2013 kode</span>
                            <div className="grid grid-cols-2 gap-3 mt-1">
                              <div>
                                <span className="text-gray-500 text-xs">Nåværende:</span>
                                <p className="text-gray-700">{improvements.metadata.isced_code.current || '(ikke satt)'}</p>
                              </div>
                              <div>
                                <span className="text-green-600 text-xs">Forslag:</span>
                                <p className="text-green-700 font-medium">{improvements.metadata.isced_code.suggested}</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{improvements.metadata.isced_code.reason}</p>
                            <button
                              type="button"
                              onClick={() => applyImprovement('metadata', improvements.metadata.isced_code.suggested, 'isced_code')}
                              className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Bruk forslag
                            </button>
                          </div>
                        )}

                        {/* Keywords */}
                        {improvements.metadata?.keywords?.needs_improvement && (
                          <div className="mt-2 p-2 bg-purple-50 rounded">
                            <span className="text-purple-600 text-xs font-medium">Nøkkelord</span>
                            <div className="mt-1">
                              <span className="text-gray-500 text-xs">Nåværende:</span>
                              <p className="text-gray-700 text-sm">
                                {(improvements.metadata.keywords.current?.length > 0)
                                  ? improvements.metadata.keywords.current.join(', ')
                                  : '(ingen)'}
                              </p>
                            </div>
                            <div className="mt-2">
                              <span className="text-green-600 text-xs">Forslag:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {improvements.metadata.keywords.suggested?.map((kw: string, idx: number) => (
                                  <span key={idx} className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">{improvements.metadata.keywords.reason}</p>
                            <button
                              type="button"
                              onClick={() => applyImprovement('metadata', improvements.metadata.keywords.suggested, 'keywords')}
                              className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Bruk forslag
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* No improvements needed message */}
                    {!improvements.target_group?.needs_improvement &&
                     !improvements.admission_requirements?.needs_improvement &&
                     !improvements.teaching_learning_methods?.needs_improvement &&
                     !improvements.description?.needs_improvement &&
                     !improvements.assessment_criteria?.needs_improvement &&
                     !improvements.learning_outcomes?.knowledge?.some((k: any) => k.needs_improvement) &&
                     !improvements.learning_outcomes?.skills?.some((s: any) => s.needs_improvement) &&
                     !improvements.learning_outcomes?.competence?.some((c: any) => c.needs_improvement) &&
                     !improvements.content_modules?.some((m: any) => m.needs_improvement) &&
                     !improvements.metadata?.eqf_level?.needs_improvement &&
                     !improvements.metadata?.esco_skills?.needs_improvement &&
                     !improvements.metadata?.isced_code?.needs_improvement &&
                     !improvements.metadata?.keywords?.needs_improvement && (
                      <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                        <CheckCircle size={16} className="inline mr-2" />
                        Studieplanen ser bra ut! Ingen spesifikke justeringer anbefalt.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Approval Status Section - integrated action area */}
          {(id || createdPlanId) && validationResult.decision && (
            <div className={`mt-4 p-4 rounded-lg border ${
              validationResult.decision === 'approved'
                ? 'bg-green-50 border-green-200'
                : validationResult.decision === 'rejected_with_revisions'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
            }`}>
              {/* Status message - only show for non-approved decisions (approved has its own prominent section below) */}
              {validationResult.decision !== 'approved' && (
                <div className="flex items-start gap-3 mb-3">
                  {validationResult.decision === 'rejected_with_revisions' ? (
                    <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  ) : (
                    <X className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                  )}
                  <div>
                    <p className={`font-medium ${
                      validationResult.decision === 'rejected_with_revisions' ? 'text-yellow-900' :
                      'text-red-900'
                    }`}>
                      {validationResult.decision === 'rejected_with_revisions' ? 'Krever begrunnelse for innsending' :
                       'Krever forbedring'}
                    </p>
                    <p className={`text-sm mt-1 ${
                      validationResult.decision === 'rejected_with_revisions' ? 'text-yellow-800' :
                      'text-red-800'
                    }`}>
                      {validationResult.feedback?.approval_status?.message ||
                       (validationResult.decision === 'rejected_with_revisions'
                         ? 'Studieplanen kan sendes inn med begrunnelse for manuell vurdering.'
                         : 'Studieplanen må forbedres før den kan sendes inn.')}
                    </p>
                  </div>
                </div>
              )}

              {/* Self-approve button for approved plans */}
              {validationResult.decision === 'approved' && currentUser?.role === 'member' && (
                <div className="mt-4 pt-4 border-t-2 border-green-300 bg-green-100 -mx-4 -mb-4 px-4 pb-4 rounded-b-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-green-900">Klar for godkjenning!</p>
                      <p className="text-sm text-green-700">Studieplanen oppfyller alle krav og kan selvgodkjennes.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowSelfApproveModal(true)}
                      className="btn bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 shadow-lg"
                    >
                      <CheckCircle size={18} />
                      Selvgodkjenn
                    </button>
                  </div>
                </div>
              )}

              {/* Admin message for plans with score 70-79 - they can approve directly */}
              {validationResult.decision === 'rejected_with_revisions' && validationResult.score >= 70 && (currentUser?.role === 'admin' || currentUser?.role === 'systemadmin') && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={16} />
                    <p className="text-sm">
                      Som {currentUser?.role === 'systemadmin' ? 'systemadministrator' : 'studieforbund-administrator'} kan du godkjenne denne studieplanen direkte med "Godkjenn"-knappen nederst.
                    </p>
                  </div>
                </div>
              )}

              {/* Inline justification form for rejected_with_revisions plans - only show for members */}
              {validationResult.decision === 'rejected_with_revisions' && currentUser?.role === 'member' && (
                <div className="mt-3 pt-3 border-t border-yellow-200">
                  <p className="text-sm text-yellow-800 mb-3">
                    Du kan sende studieplanen til studieforbundet for manuell vurdering.
                    Oppgi en begrunnelse for hvorfor planen bør godkjennes til tross for KI-tilbakemeldingen.
                  </p>

                  <div className="mb-3">
                    <label className="label text-sm font-medium text-yellow-900 mb-1">
                      Begrunnelse for innsending *
                    </label>
                    <textarea
                      className="input bg-white border-yellow-300 focus:border-yellow-500"
                      rows={3}
                      placeholder="Forklar hvorfor studieplanen bør vurderes av studieforbundet..."
                      value={submissionJustification}
                      onChange={(e) => setSubmissionJustification(e.target.value)}
                    />
                    <p className="text-xs text-yellow-700 mt-1">
                      Minimum 50 tegn ({submissionJustification.length}/50)
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmitForReview}
                    disabled={submittingForReview || submissionJustification.trim().length < 50}
                    className="btn bg-yellow-600 hover:bg-yellow-700 text-white flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {submittingForReview ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <AlertTriangle size={18} />
                    )}
                    Send til vurdering hos studieforbundet
                  </button>
                </div>
              )}

              {/* Message for fully rejected plans */}
              {validationResult.decision === 'rejected' && (
                <p className="text-sm text-red-700 mt-2">
                  Bruk KI-forslagene ovenfor for å forbedre studieplanen, og kjør deretter ny validering.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ESCO-kvalitetsvalidering */}
      {escoValidation && (escoValidation.dropped_count > 0 || escoValidation.warned_count > 0) && (
        <div className="border rounded-lg p-6 mb-6 bg-kf-orange-50 border-kf-orange-300">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="text-kf-orange-600 flex-shrink-0 mt-1" size={24} />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-kf-orange-900">
                ESCO-kvalitetssjekk: {escoValidation.dropped_count + escoValidation.warned_count} koblinger trenger oppmerksomhet
              </h3>
              <p className="text-sm text-kf-orange-800 mt-1">
                Vi har sjekket de lagrede ESCO-koblingene mot 4 kriterier (fagområde, Bloom-nivå, omfang, pillar).
                <strong className="ml-1">Bedre å mangle en kobling enn å ha en feilaktig kobling som ender i sertifikatet.</strong>
              </p>
              <div className="text-xs text-kf-orange-700 mt-2">
                Av {escoValidation.summary.total} totalt:
                <span className="font-medium text-green-700 ml-1">{escoValidation.summary.accepted} akseptert</span>
                {escoValidation.warned_count > 0 && <>, <span className="font-medium text-yellow-700">{escoValidation.warned_count} varsel</span></>}
                {escoValidation.dropped_count > 0 && <>, <span className="font-medium text-red-700">{escoValidation.dropped_count} automatisk fjernet</span></>}
              </div>
            </div>
          </div>

          {/* Droppete koblinger (info-only) */}
          {escoValidation.dropped_count > 0 && (
            <div className="bg-white rounded-lg border border-red-200 p-4 mb-4">
              <h4 className="font-semibold text-red-900 text-sm mb-2 flex items-center gap-2">
                <X size={16} /> Automatisk fjernet ({escoValidation.dropped_count})
              </h4>
              <p className="text-xs text-gray-600 mb-3">
                Disse koblingene hadde flere kvalitetsproblemer og er allerede fjernet fra studieplanen.
                Du kan kjøre KI-forslag for å finne bedre koblinger til de samme læringsutbyttene.
              </p>
              <ul className="space-y-2">
                {escoValidation.dropped.map((m, idx) => (
                  <li key={`dropped-${idx}`} className="text-sm border-l-2 border-red-300 pl-3">
                    <div className="font-medium text-red-800">{m.escoTitle}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Knyttet til: <span className="italic">{(m.outcomeText || '').slice(0, 80)}{(m.outcomeText || '').length > 80 ? '…' : ''}</span>
                    </div>
                    {m.reasons && m.reasons.length > 0 && (
                      <div className="text-xs text-red-600 mt-1">
                        Årsak: {m.reasons.join(' · ')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Varslete koblinger (krever handling) */}
          {escoValidation.warned_count > 0 && (
            <div className="bg-white rounded-lg border border-yellow-300 p-4">
              <h4 className="font-semibold text-yellow-900 text-sm mb-2 flex items-center gap-2">
                <AlertTriangle size={16} /> Krever bekreftelse ({escoValidation.warned_count})
              </h4>
              <p className="text-xs text-gray-600 mb-3">
                Disse koblingene har ett kvalitetsflagg. Bekreft hvis koblingen er riktig, eller fjern den.
                <strong className="ml-1">Selvgodkjenning er blokkert til alle er behandlet.</strong>
              </p>
              <ul className="space-y-3">
                {escoValidation.warned.map((m, idx) => {
                  const mappingKey = `${m.outcomeType}-${m.outcomeIndex}-${m.escoUri}`;
                  const isResolving = resolvingEsco === mappingKey;
                  return (
                    <li key={`warned-${idx}`} className="border border-yellow-200 rounded p-3">
                      <div className="font-medium text-yellow-900 text-sm">{m.escoTitle}</div>
                      {m.escoDescription && (
                        <div className="text-xs text-gray-600 mt-1 italic">
                          {m.escoDescription.slice(0, 200)}{m.escoDescription.length > 200 ? '…' : ''}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        Knyttet til: <span className="italic">{(m.outcomeText || '').slice(0, 120)}{(m.outcomeText || '').length > 120 ? '…' : ''}</span>
                      </div>
                      {m.reasons && m.reasons.length > 0 && (
                        <div className="text-xs text-yellow-700 mt-1 font-medium">
                          ⚠ {m.reasons.join(' · ')}
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          onClick={async () => {
                            const planId = id || createdPlanId;
                            if (!planId) return;
                            setResolvingEsco(mappingKey);
                            try {
                              const res = await authenticatedFetch(`/api/study-plans/${planId}/esco-mappings/resolve-warning`, {
                                method: 'POST',
                                body: JSON.stringify({
                                  outcomeType: m.outcomeType,
                                  outcomeIndex: m.outcomeIndex,
                                  escoUri: m.escoUri,
                                  action: 'confirm'
                                })
                              });
                              if (!res.ok) throw new Error('Kunne ikke bekrefte');
                              const data = await res.json();
                              setEscoValidation(prev => prev ? {
                                ...prev,
                                warned: prev.warned.filter((_, i) => i !== idx),
                                warned_count: prev.warned_count - 1,
                                summary: { ...prev.summary, accepted: prev.summary.accepted + 1, warnings: prev.summary.warnings - 1, blocks_approval: (prev.summary.warnings - 1 > 0 || prev.dropped_count > 0) }
                              } : null);
                              if (data.decision_reverted && data.validation_decision) {
                                setValidationResult(prev => prev ? { ...prev, decision: data.validation_decision } : prev);
                                toast.showSuccess('Kobling bekreftet — studieplanen kan nå selvgodkjennes');
                              } else {
                                toast.showSuccess('Kobling bekreftet');
                              }
                            } catch (e: any) {
                              toast.showError('Kunne ikke bekrefte: ' + e.message);
                            } finally {
                              setResolvingEsco(null);
                            }
                          }}
                          disabled={isResolving}
                          className="text-xs px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300 flex items-center gap-1"
                        >
                          {isResolving ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle size={12} />}
                          Bekreft kobling
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            const planId = id || createdPlanId;
                            if (!planId) return;
                            const confirmed = await confirm({
                              title: 'Fjern ESCO-kobling?',
                              message: `Fjern koblingen "${m.escoTitle}" fra dette læringsutbyttet?`,
                              confirmLabel: 'Fjern',
                              variant: 'danger'
                            });
                            if (!confirmed) return;
                            setResolvingEsco(mappingKey);
                            try {
                              const res = await authenticatedFetch(`/api/study-plans/${planId}/esco-mappings/resolve-warning`, {
                                method: 'POST',
                                body: JSON.stringify({
                                  outcomeType: m.outcomeType,
                                  outcomeIndex: m.outcomeIndex,
                                  escoUri: m.escoUri,
                                  action: 'remove'
                                })
                              });
                              if (!res.ok) throw new Error('Kunne ikke fjerne');
                              const data = await res.json();
                              setEscoValidation(prev => prev ? {
                                ...prev,
                                warned: prev.warned.filter((_, i) => i !== idx),
                                warned_count: prev.warned_count - 1,
                                summary: { ...prev.summary, warnings: prev.summary.warnings - 1, blocks_approval: (prev.summary.warnings - 1 > 0 || prev.dropped_count > 0) }
                              } : null);
                              // Fjern også fra chip-listen på læringsutbyttet
                              setOutcomeEscoMappings(prev =>
                                prev
                                  .map(mapping => {
                                    if (mapping.outcomeType !== m.outcomeType || mapping.outcomeIndex !== m.outcomeIndex) return mapping;
                                    return { ...mapping, escoSkills: (mapping.escoSkills || []).filter(s => s.uri !== m.escoUri) };
                                  })
                                  .filter(mapping => (mapping.escoSkills || []).length > 0)
                              );
                              if (data.decision_reverted && data.validation_decision) {
                                setValidationResult(prev => prev ? { ...prev, decision: data.validation_decision } : prev);
                                toast.showSuccess('Kobling fjernet — studieplanen kan nå selvgodkjennes');
                              } else {
                                toast.showSuccess('Kobling fjernet');
                              }
                            } catch (e: any) {
                              toast.showError('Kunne ikke fjerne: ' + e.message);
                            } finally {
                              setResolvingEsco(null);
                            }
                          }}
                          disabled={isResolving}
                          className="text-xs px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300 flex items-center gap-1"
                        >
                          {isResolving ? <Loader2 className="animate-spin" size={12} /> : <X size={12} />}
                          Fjern kobling
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Task #321 (2026-07-03): brukt <div> istedet for <form>. StudyPlanForm
          har ingen native form-submit (alle knapper er type="button" med onClick),
          og nested submit-knapper i modaler boblet opp og reloadet siden. */}
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Grunnleggende informasjon</h2>

          <div className="space-y-4">
            <div>
              <label className="label">Tittel *</label>
              <input
                type="text"
                className="input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="label">Beskrivelse *</label>
              <textarea
                className="input"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label flex items-center gap-1">Kompetansepoeng (ECTS-skala) * <HelpTooltip slug="scope-classification" size="sm" /></label>
                <input
                  type="number"
                  className="input"
                  min="0"
                  step="0.01"
                  value={formData.ects_credits}
                  onChange={(e) => setFormData({ ...formData, ects_credits: parseFloat(e.target.value) })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  * 1 Kompetansepoeng tilsvarer 1 ECTS = 25–30 timers samlet læringsarbeid.
                  <br />
                  Oppdateres automatisk fra timer. Kan overstyres manuelt.
                </p>
              </div>

              <div>
                <label className="label">Estimerte timer *</label>
                <input
                  type="number"
                  className="input"
                  min="4"
                  value={formData.estimated_hours}
                  onChange={(e) => setFormData({ ...formData, estimated_hours: parseInt(e.target.value) })}
                  onBlur={(e) => {
                    const hours = parseInt(e.target.value, 10);
                    if (hours >= 4) {
                      // Task #330 (2026-07-03): auto-oppdater ECTS ved hver
                      // timer-endring (ikke bare når tomt). Da unngår vi at
                      // eksisterende ECTS-verdier blir usynkronisert med timer.
                      // 1 ECTS = 27.5 timer (midt-verdi). Under 1 ECTS: 0.05-
                      // presisjon så små kurs treffer riktig range.
                      const raw = hours / 27.5;
                      const suggestedEcts = raw < 1
                        ? Math.max(0.05, Math.round(raw * 20) / 20)
                        : Math.round(raw * 10) / 10;
                      if (Math.abs(suggestedEcts - (formData.ects_credits || 0)) > 0.001) {
                        setFormData(prev => ({ ...prev, ects_credits: suggestedEcts }));
                      }
                    }
                  }}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 4 timer for Kompetanseattest. {formData.ects_credits > 0 && `Anbefalt: ${Math.round(formData.ects_credits * 25)}–${Math.round(formData.ects_credits * 30)} timer.`}
                </p>
              </div>
            </div>

            {/* Target group with ESCO occupation matching */}
            <OccupationMatcher
              value={formData.target_group || ''}
              onChange={(value) => {
                setFormData({ ...formData, target_group: value });
                setHasUnsavedChanges(true);
              }}
              matchedOccupations={targetGroupOccupations}
              onOccupationsChange={(occupations) => {
                setTargetGroupOccupations(occupations);
                setHasUnsavedChanges(true);
              }}
              label="Målgruppe"
              placeholder="Beskriv hvem kurset er rettet mot, f.eks. 'Voksne uten formell utdanning innen fagfeltet' eller 'Ansatte i helsesektoren med grunnleggende IKT-kompetanse'"
              helpText="Yrkesroller identifisert i teksten kobles automatisk til ESCO-klassifiseringen."
              rows={2}
            />

            {/* Admission requirements with ESCO occupation matching */}
            <OccupationMatcher
              value={formData.admission_requirements || ''}
              onChange={(value) => {
                setFormData({ ...formData, admission_requirements: value });
                setHasUnsavedChanges(true);
              }}
              matchedOccupations={admissionOccupations}
              onOccupationsChange={(occupations) => {
                setAdmissionOccupations(occupations);
                setHasUnsavedChanges(true);
              }}
              label="Opptakskrav"
              placeholder="Eventuelle forkunnskaper eller formelle krav, f.eks. 'Ingen formelle opptakskrav' eller 'Grunnleggende datakunnskaper'"
              helpText="Eventuelle yrkeskrav identifiseres og kobles til ESCO."
              rows={2}
            />

            <div>
              <label className="label flex items-center gap-1">Undervisnings- og læringsformer <HelpTooltip slug="teaching-methods" size="sm" /></label>
              <textarea
                className="input"
                rows={3}
                value={formData.teaching_learning_methods || ''}
                onChange={(e) => setFormData({ ...formData, teaching_learning_methods: e.target.value })}
                placeholder="Beskriv hvordan undervisningen gjennomføres, f.eks. 'Nettbasert læring med videoforelesninger, praktiske øvelser og gruppearbeid'"
              />
            </div>

            {/* Kategorier for kompetanseheving.no (forslag på studieplan-nivå, overstyres per kurs) */}
            <div>
              <label className="label">Foreslåtte kategorier (kompetanseheving.no)</label>
              <p className="text-xs text-gray-600 mb-2">
                Kategoriene er <strong>forslag</strong> som arves som default når kurs opprettes fra denne studieplanen.
                Hvert kurs kan overstyre listen ved opprettelse — så en helse-studieplan kan f.eks. markedsføres i "Ledelse" hvis kurset retter seg mot avdelingsledere.
                <strong>Første valgte er primær</strong> for hovedsortering.
              </p>

              {/* Forslag basert på yrkesroller */}
              {suggestedCategories.length > 0 && (
                <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded">
                  <div className="text-xs font-medium text-purple-900 mb-1">
                    💡 Forslag basert på ESCO-yrkesroller — klikk for å legge til:
                  </div>
                  <div className="text-[11px] text-purple-700 mb-2 italic">
                    Forslagene er automatiske og kan være upresise. Vurder hvert forslag før du legger det til, og avvis det som ikke passer studieplanen.
                  </div>
                  <div className="flex flex-col gap-2">
                    {suggestedCategories
                      .filter(s => !(formData.categories ?? []).includes(s.category))
                      .map(s => (
                        <div key={s.category} className="flex items-start gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const newCats = [...(formData.categories ?? []), s.category];
                              setFormData({ ...formData, categories: newCats });
                              setHasUnsavedChanges(true);
                            }}
                            className="text-xs px-2.5 py-1 bg-white border border-purple-300 hover:bg-purple-100 text-purple-900 rounded-full inline-flex items-center gap-1 flex-shrink-0"
                            title={`Legg til kategorien "${s.category}"`}
                          >
                            + Legg til «{s.category}»
                          </button>
                          {s.matched.length > 0 && (
                            <span className="text-[11px] text-purple-700 leading-relaxed pt-1">
                              foreslått fra yrkesrolle: <em>{s.matched.join(', ')}</em>
                            </span>
                          )}
                        </div>
                      ))}
                    {(formData.categories ?? []).length > 0 && suggestedCategories.every(s => (formData.categories ?? []).includes(s.category)) && (
                      <span className="text-xs text-purple-700 italic">Alle forslag er allerede valgt</span>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-gray-50 rounded border border-gray-200">
                {[
                  'Helse', 'Førstehjelp', 'Ernæring',
                  'Jus', 'Personvern', 'Arbeidsrett',
                  'Ledelse', 'Styrearbeid', 'Prosjektledelse',
                  'HMS', 'Brannvern',
                  'Økonomi', 'Skatt',
                  'IT', 'Programmering',
                  'Kommunikasjon', 'Markedsføring',
                  'Barne- og ungdomsarbeid',
                  'Språk', 'Kultur', 'Hobby'
                ].map((cat) => {
                  const idx = (formData.categories ?? []).indexOf(cat);
                  const checked = idx !== -1;
                  const isPrimary = idx === 0;
                  return (
                    <label key={cat} className={`flex items-center gap-2 text-sm p-1 rounded cursor-pointer hover:bg-white ${isPrimary ? 'bg-kf-light' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          let newCats: string[];
                          if (e.target.checked) {
                            newCats = [...(formData.categories ?? []), cat];
                          } else {
                            newCats = (formData.categories ?? []).filter(c => c !== cat);
                          }
                          setFormData({ ...formData, categories: newCats });
                          setHasUnsavedChanges(true);
                        }}
                        className="rounded border-gray-300"
                      />
                      <span>{cat}</span>
                      {isPrimary && <span className="text-xs text-kf-primary font-semibold ml-auto">PRIMÆR</span>}
                    </label>
                  );
                })}
              </div>
              {(formData.categories ?? []).length > 0 && (
                <p className="text-xs text-gray-600 mt-2">
                  Valgt: {(formData.categories ?? []).join(', ')}
                  {(formData.categories ?? []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        // Swap første og andre for å endre primær
                        const cats = [...(formData.categories ?? [])];
                        [cats[0], cats[1]] = [cats[1], cats[0]];
                        setFormData({ ...formData, categories: cats });
                        setHasUnsavedChanges(true);
                      }}
                      className="ml-2 text-kf-primary hover:underline"
                    >
                      (bytt primær)
                    </button>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Learning Outcomes with ESCO and Bloom's Taxonomy */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Læringsutbytte</h2>
          <p className="text-sm text-gray-500 mb-4">
            Hvert læringsutbytte analyseres for Bloom's taksonomi-nivå og kobles automatisk til ESCO-ferdigheter.
          </p>
          <LearningOutcomeEditor
            learning_outcomes={formData.learning_outcomes}
            onChange={(outcomes) => {
              setFormData({ ...formData, learning_outcomes: outcomes });
              setHasUnsavedChanges(true);
              setNeedsRevalidation(true);
            }}
            escoMappings={outcomeEscoMappings}
            onESCOMappingsChange={(mappings) => {
              setOutcomeEscoMappings(mappings);
              setHasUnsavedChanges(true);
            }}
            categoryWarnings={outcomeCategoryWarnings}
            eqfLevel={parseInt(formData.metadata?.eqf_level || '5', 10)}
            ectsCredits={formData.ects_credits}
            showAutoSuggest={true}
            studyPlanContext={`${formData.title || ''} - ${formData.description || ''}`}
            occupationUris={Array.from(new Set([
              ...((targetGroupOccupations || []).map((o: any) => o?.uri).filter(Boolean)),
              ...((admissionOccupations || []).map((o: any) => o?.uri).filter(Boolean))
            ]))}
          />
        </div>

        {/* Content Modules */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Innholdsmoduler</h2>
            <button
              type="button"
              onClick={() => setShowModuleSection(!showModuleSection)}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800"
            >
              <Layers size={16} />
              {showModuleSection ? 'Skjul detaljerte moduler' : 'Vis detaljerte moduler'}
            </button>
          </div>

          {/* Simple text-based modules (legacy) */}
          {!showModuleSection && (
            <>
              <div className="space-y-2 mb-4">
                <textarea
                  className="input w-full min-h-[80px] resize-y"
                  placeholder="Skriv innholdsmodul her..."
                  value={moduleInput}
                  onChange={(e) => setModuleInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      e.preventDefault();
                      addModule();
                    }
                  }}
                />
                <div className="flex justify-end">
                  <button type="button" onClick={addModule} className="btn btn-secondary">
                    Legg til modul
                  </button>
                </div>
                <p className="text-xs text-gray-500">Tips: Trykk Ctrl+Enter for å legge til raskt</p>
              </div>
              <ul className="space-y-2">
                {formData.content_modules.map((module, index) => (
                  <li key={index} className="bg-gray-50 p-3 rounded">
                    {editingItem?.field === 'module' && editingItem.index === index ? (
                      <div className="space-y-2">
                        <textarea
                          className="input w-full min-h-[100px] resize-y"
                          value={editingItem.value}
                          onChange={(e) => setEditingItem({...editingItem, value: e.target.value})}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setEditingItem(null);
                            }
                          }}
                          autoFocus
                          placeholder="Skriv innholdsmodul her..."
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => setEditingItem(null)}
                            className="btn btn-secondary text-sm py-1 px-3"
                          >
                            Avbryt
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              updateModule(index, editingItem.value);
                              setEditingItem(null);
                            }}
                            className="btn btn-primary text-sm py-1 px-3"
                          >
                            Lagre
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <span
                          className="flex-1 cursor-pointer hover:text-kf-primary whitespace-pre-wrap"
                          onClick={() => {
                            // v6.4+: moduler kan være objekter. For redigering i det
                            // enkle legacy-feltet gir vi JSON som fallback — strukturerte
                            // moduler bør redigeres i ModuleEditor nedenfor i stedet.
                            const editValue = typeof module === 'object' && module !== null
                              ? JSON.stringify(module, null, 2)
                              : String(module);
                            setEditingItem({field: 'module', index, value: editValue});
                          }}
                          title={typeof module === 'object' ? 'Strukturert modul — bruk modul-editoren nedenfor for å redigere riktig' : 'Klikk for å redigere'}
                        >
                          {typeof module === 'object' && module !== null
                            ? ((module as any).title || `Modul ${index + 1}`) +
                              ((module as any).description ? ' — ' + (module as any).description : '')
                            : String(module)}
                        </span>
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              const editValue = typeof module === 'object' && module !== null
                                ? JSON.stringify(module, null, 2)
                                : String(module);
                              setEditingItem({field: 'module', index, value: editValue});
                            }}
                            className="text-gray-500 hover:text-kf-primary p-1"
                            title="Rediger"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeModule(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Fjern"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Detailed module editor (for modular completion) */}
          {showModuleSection && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-4">
                Bruk detaljerte moduler for å tillate kandidater å fullføre deler av studieplanen separat.
                Hver modul kan ha egne læringsutbytte, kompetansepoeng og litteraturreferanser.
              </p>
              <ModuleEditor
                studyPlanId={id}
                planDataForSuggest={!id ? {
                  title: formData.title,
                  description: formData.description,
                  ects_credits: formData.ects_credits,
                  estimated_hours: formData.estimated_hours,
                  target_group: formData.target_group,
                  admission_requirements: formData.admission_requirements,
                  learning_outcomes: formData.learning_outcomes,
                  metadata: { eqf_level: formData.metadata?.eqf_level }
                } : undefined}
                modules={modules}
                onChange={setModules}
                parentLearningOutcomes={formData.learning_outcomes}
                parentEscoMappings={outcomeEscoMappings}
                totalEcts={formData.ects_credits}
                totalHours={formData.estimated_hours}
                disabled={studyPlanStatus === 'approved'}
              />
            </div>
          )}
        </div>

        {/* Assessment */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Vurdering</h2>
          <div>
            <label className="label flex items-center gap-1">Vurderingskriterier * <HelpTooltip slug="assessment-methods" size="sm" /></label>
            <textarea
              className="input"
              rows={4}
              value={formData.assessment_criteria}
              onChange={(e) => setFormData({ ...formData, assessment_criteria: e.target.value })}
              required
            />
          </div>
        </div>

        {/* Fase 2.6 Blokk D: Vurderingsramme */}
        <AssessmentFrameworkEditor
          value={assessmentFramework}
          onChange={setAssessmentFramework}
          disabled={studyPlanStatus === 'approved'}
        />

        {/* Metadata */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Metadata</h2>
          <div className="space-y-4">
            <div>
              <label className="label flex items-center gap-1">EQF-nivå <HelpTooltip slug="eqf-nkr-level" size="sm" /></label>
              <select
                className="input"
                value={formData.metadata.eqf_level}
                onChange={(e) => setFormData({
                  ...formData,
                  metadata: { ...formData.metadata, eqf_level: e.target.value as any }
                })}
              >
                {['1', '2', '3', '4', '5', '6', '7', '8'].map((level) => (
                  <option key={level} value={level}>Nivå {level}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">ISCED-F Fagområde (valgfritt)</label>
              <IscedFSelect
                value={formData.isced_f_code || null}
                onChange={(code) => setFormData({
                  ...formData,
                  isced_f_code: code
                })}
              />
              <p className="text-sm text-gray-600 mt-1">
                🇪🇺 ISCED-F 2013 klassifisering brukes i Europass Digital Credentials for europeisk anerkjennelse
              </p>
            </div>

            {/* Keywords Section */}
            <div>
              <label className="label">Nøkkelord</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(formData.metadata?.keywords || []).map((keyword: string, idx: number) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-sm border border-purple-200"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => {
                        const newKeywords = [...(formData.metadata?.keywords || [])];
                        newKeywords.splice(idx, 1);
                        setFormData({
                          ...formData,
                          metadata: { ...formData.metadata, keywords: newKeywords }
                        });
                        setHasUnsavedChanges(true);
                      }}
                      className="hover:text-purple-900"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="Legg til nøkkelord..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const input = e.target as HTMLInputElement;
                      const value = input.value.trim();
                      if (value && !(formData.metadata?.keywords || []).includes(value)) {
                        setFormData({
                          ...formData,
                          metadata: {
                            ...formData.metadata,
                            keywords: [...(formData.metadata?.keywords || []), value]
                          }
                        });
                        setHasUnsavedChanges(true);
                        input.value = '';
                      }
                    }
                  }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Trykk Enter for å legge til. Nøkkelord brukes for søk og kategorisering.
              </p>
            </div>

            {/* ESCO Skills Section - Collected from Learning Outcomes */}
            <div className="border border-kf-blue-200 rounded-lg p-4 bg-kf-blue-50">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <label className="label flex items-center gap-2 mb-1">
                    <Link2 size={16} className="text-kf-blue-600" />
                    <span>ESCO-ferdigheter</span>
                  </label>
                  <p className="text-sm text-gray-600">
                    🇪🇺 Ferdigheter fra EUs ESCO-klassifisering, koblet til læringsutbyttene ovenfor.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAutoSuggestEscoForOutcomes}
                  disabled={autoSuggestingEscoForOutcomes || studyPlanStatus === 'approved'}
                  className="btn btn-secondary text-sm flex items-center gap-2 flex-shrink-0"
                  title="Bruker tittel, beskrivelse og læringsutbytter til å foreslå ESCO-ferdigheter. Krever ikke lagring."
                >
                  {autoSuggestingEscoForOutcomes ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Analyserer...
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} />
                      KI-foreslå ESCO
                    </>
                  )}
                </button>
              </div>

              {/* Collected ESCO skills from learning outcome mappings */}
              {(() => {
                // ESCO v2.2: Only show confirmed skills (what will actually be saved)
                const confirmedMappings = filterConfirmedESCOMappings(outcomeEscoMappings);

                // Collect all unique ESCO skills from outcome mappings
                const collectedSkills = new Map<string, { title: string; skillType?: string; category: string }>();

                confirmedMappings.forEach(mapping => {
                  mapping.escoSkills.forEach(skill => {
                    if (!collectedSkills.has(skill.uri)) {
                      collectedSkills.set(skill.uri, {
                        title: skill.title,
                        skillType: skill.skillType,
                        category: mapping.outcomeType
                      });
                    }
                  });
                });

                const skillsList = Array.from(collectedSkills.entries());
                const totalCount = skillsList.length + selectedEscoSkills.length;

                return (
                  <>
                    {skillsList.length > 0 ? (
                      <div className="bg-white border border-kf-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-xs font-medium text-kf-blue-700 mb-2">
                          Fra læringsutbytter ({skillsList.length}):
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {skillsList.map(([uri, skill]) => (
                            <span
                              key={uri}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${
                                skill.category === 'knowledge'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : skill.category === 'skills'
                                  ? 'bg-kf-blue-50 text-kf-blue-700 border-kf-blue-200'
                                  : 'bg-violet-50 text-violet-700 border-violet-200'
                              }`}
                              title={`Fra ${skill.category === 'knowledge' ? 'kunnskap' : skill.category === 'skills' ? 'ferdigheter' : 'kompetanse'}`}
                            >
                              <Link2 size={10} />
                              {skill.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-yellow-800">
                          <strong>Ingen ESCO-ferdigheter koblet ennå.</strong> Bruk "Auto-foreslå ESCO"-knappen under Læringsutbytte for å koble ferdigheter automatisk.
                        </p>
                      </div>
                    )}

                    {/* Additional manually added skills */}
                    {selectedEscoSkills.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          Manuelt lagt til ({selectedEscoSkills.length}):
                        </p>
                        <div className="space-y-1.5">
                          {selectedEscoSkills.map((skill) => (
                            <div
                              key={skill.uri}
                              className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm"
                            >
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Link2 size={12} className="text-gray-400 flex-shrink-0" />
                                <span className="truncate">{skill.title}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeEscoSkill(skill.uri)}
                                className="text-red-500 hover:text-red-700 flex-shrink-0"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Summary */}
                    {totalCount > 0 && (
                      <p className="text-xs text-gray-500 mb-3">
                        Totalt {totalCount} ESCO-ferdigheter vil inkluderes i attesten.
                      </p>
                    )}
                  </>
                );
              })()}

              {/* Expandable section to add more skills */}
              <details className="mt-2">
                <summary className="cursor-pointer text-sm text-kf-blue-600 hover:text-kf-blue-800 flex items-center gap-1">
                  <Plus size={14} />
                  Legg til flere ESCO-ferdigheter manuelt
                </summary>
                <div className="mt-3 pt-3 border-t border-kf-blue-200">
                  <div className="relative">
                    <input
                      type="text"
                      className="input pr-8"
                      placeholder="Søk etter ESCO skills..."
                      value={escoSearchQuery}
                      onChange={(e) => setEscoSearchQuery(e.target.value)}
                    />
                    {escoSearching && (
                      <Loader2 className="absolute right-2 top-1/2 transform -translate-y-1/2 animate-spin text-gray-400" size={16} />
                    )}
                  </div>

                  {/* Search Results */}
                  {escoSearchResults.length > 0 && (
                    <div className="mt-2 border border-gray-300 rounded-lg bg-white max-h-64 overflow-y-auto">
                      {escoSearchResults.map((skill) => {
                        // Task #366 (2026-08-20): norsk badge for skill-type
                        // (samme mønster som LearningOutcomeEditor task #358)
                        const info: Record<string, { label: string; className: string }> = {
                          'knowledge': { label: 'Kunnskap', className: 'bg-kf-blue-100 text-kf-blue-800 border-kf-blue-300' },
                          'skill/competence': { label: 'Ferdighet/kompetanse', className: 'bg-green-100 text-green-800 border-green-300' },
                          'skill': { label: 'Ferdighet', className: 'bg-emerald-100 text-emerald-800 border-emerald-300' }
                        };
                        const typeInfo = skill.skillType ? info[skill.skillType] : null;
                        return (
                          <button
                            key={skill.uri}
                            type="button"
                            onClick={() => addEscoSkill(skill)}
                            className="w-full text-left px-3 py-2 hover:bg-kf-blue-50 border-b border-gray-200 last:border-b-0"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="font-medium text-sm flex-1">{skill.title}</div>
                              {typeInfo && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${typeInfo.className} flex-shrink-0`}>
                                  {typeInfo.label}
                                </span>
                              )}
                            </div>
                            {skill.description && (
                              <div className="text-xs text-gray-600 mt-1 whitespace-pre-line">{skill.description}</div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </details>
            </div>

            <div>
              <label className="label flex items-center gap-1">Vurderingsmetoder <HelpTooltip slug="assessment-methods" size="sm" /></label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="Legg til metode..."
                  value={assessmentMethodInput}
                  onChange={(e) => setAssessmentMethodInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAssessmentMethod();
                    }
                  }}
                />
                <button type="button" onClick={addAssessmentMethod} className="btn btn-secondary">
                  Legg til
                </button>
              </div>
              <ul className="space-y-1">
                {formData.metadata.assessment_methods?.map((method, index) => (
                  <li key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm gap-2">
                    {editingItem?.field === 'assessment' && editingItem.index === index ? (
                      <input
                        type="text"
                        className="input flex-1 text-sm"
                        value={editingItem.value}
                        onChange={(e) => setEditingItem({...editingItem, value: e.target.value})}
                        onBlur={() => {
                          updateAssessmentMethod(index, editingItem.value);
                          setEditingItem(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateAssessmentMethod(index, editingItem.value);
                            setEditingItem(null);
                          } else if (e.key === 'Escape') {
                            setEditingItem(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="flex-1 cursor-pointer hover:text-kf-primary"
                        onClick={() => setEditingItem({field: 'assessment', index, value: method})}
                        title="Klikk for å redigere"
                      >
                        {method}
                      </span>
                    )}
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditingItem({field: 'assessment', index, value: method})}
                        className="text-gray-500 hover:text-kf-primary p-1"
                        title="Rediger"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            metadata: {
                              ...formData.metadata,
                              assessment_methods: formData.metadata.assessment_methods?.filter((_, i) => i !== index),
                            },
                          });
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Fjern"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Literature */}
        {id && (
          <div className="card">
            <LiteratureManager
              studyPlanId={id}
              readOnly={studyPlanStatus === 'approved' && !needsRevalidation}
              relevanceWarnings={(validationResult?.feedback as any)?.literature_relevance_warnings || []}
              onRelevanceConfirmed={(linkId) => {
                // Fjern varselet lokalt for umiddelbar UX-oppdatering
                setValidationResult(prev => {
                  if (!prev?.feedback) return prev;
                  const fb: any = prev.feedback;
                  if (Array.isArray(fb.literature_relevance_warnings)) {
                    return {
                      ...prev,
                      feedback: {
                        ...fb,
                        literature_relevance_warnings: fb.literature_relevance_warnings.filter(
                          (w: any) => w.link_id !== linkId
                        )
                      }
                    };
                  }
                  return prev;
                });
              }}
              onLiteratureChange={(opts) => {
                // Task #348 (2026-07-07): litteratur-endring skal alltid
                // trigge revalidering hvis planen har vært validert.
                // Litteratur-relevans er del av /validate-flyten (endringssett
                // AN), så bruker må kjøre ny validering før godkjenning.
                if (validationResult || studyPlanStatus === 'approved') {
                  setNeedsRevalidation(true);
                }
                // Task #383 (2026-07-07): sync dbUpdatedAt slik at neste
                // "Lagre utkast" ikke gir falsk 409-konflikt fordi
                // discover/accept eller literature-mutasjoner har oppdatert
                // study_plans.updated_at.
                if (opts?.updated_at) {
                  setDbUpdatedAt(new Date(opts.updated_at).toISOString());
                }
              }}
            />
          </div>
        )}

        {/* Translation - only for approved study plans */}
        {id && studyPlanStatus === 'approved' && (
          <div className="card">
            <TranslationEditor
              studyPlan={{
                id,
                title: formData.title,
                description: formData.description,
                target_group: formData.target_group || '',
                admission_requirements: formData.admission_requirements || '',
                teaching_learning_methods: formData.teaching_learning_methods || '',
                assessment_criteria: formData.assessment_criteria,
                learning_outcomes: formData.learning_outcomes,
                content_modules: formData.content_modules,
                status: studyPlanStatus
              }}
            />
          </div>
        )}

        {/* Error message near save button */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Validation confirmation info box - shows when user clicks KI-validering with unsaved changes */}
        {showValidationConfirm && (
          <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-kf-blue-100 rounded-full flex items-center justify-center">
                <Save size={20} className="text-kf-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-kf-blue-900 mb-1">Lagring av endringer</h4>
                <p className="text-sm text-kf-blue-800 mb-3">
                  Du har ulagrede endringer. Når du kjører KI-validering, vil endringene dine lagres automatisk først,
                  og deretter valideres den oppdaterte studieplanen.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleValidate}
                    className="btn bg-kf-blue-600 hover:bg-kf-blue-700 text-white text-sm flex items-center gap-2"
                  >
                    <Sparkles size={16} />
                    Lagre og valider
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowValidationConfirm(false)}
                    className="btn btn-secondary text-sm"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success-melding over knappene (task #343) */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-green-600" size={20} />
              <span>{successMessage}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-4 flex-wrap">
          <button
            type="button"
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="btn btn-secondary flex items-center gap-2"
          >
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            Lagre utkast
          </button>

          {/* Task #348 (2026-07-07): fire-sone modell med terskler fra DB.
              Score-soner:
                ≥ auto_approve                              → grønn (selvgodkjenn uten begrunnelse)
                ≥ self_approve_with_justification (< auto)  → lysegrønn (selvgodkjenn MED begrunnelse)
                ≥ manual_review (< self_approve_with_just)  → gul (send til studieforbund med begrunnelse)
                < manual_review                             → rød (avvist, må forbedres)
          */}

          {/* MEMBER — sone 1: grønn, selvgodkjenn uten begrunnelse */}
          {(id || createdPlanId) && currentUser?.role === 'member' && validationResult && validationRunThisSession &&
           validationResult.score >= thresholds.auto_approve_threshold &&
           !needsRevalidation && !hasUnsavedChanges && (
            <button
              type="button"
              onClick={() => setShowSelfApproveModal(true)}
              disabled={approving || saving}
              className="btn btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              {approving ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
              Godkjenn selv
            </button>
          )}

          {/* MEMBER — sone 2: lysegrønn, selvgodkjenn MED begrunnelse */}
          {(id || createdPlanId) && currentUser?.role === 'member' && validationResult && validationRunThisSession &&
           validationResult.score >= thresholds.self_approve_with_justification_threshold &&
           validationResult.score < thresholds.auto_approve_threshold &&
           !needsRevalidation && !hasUnsavedChanges && (
            <div className="relative group">
              <button
                type="button"
                onClick={() => setShowSelfApproveModal(true)}
                disabled={approving || saving}
                className="btn btn-primary flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600"
              >
                {approving ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
                Godkjenn selv (med begrunnelse)
              </button>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                Score {validationResult.score.toFixed(0)} — krever begrunnelse for selvgodkjenning
              </div>
            </div>
          )}

          {/* MEMBER — sone 3: gul, send til studieforbund med begrunnelse */}
          {currentUser?.role === 'member' && validationRunThisSession && validationResult &&
           validationResult.score >= thresholds.manual_review_threshold &&
           validationResult.score < thresholds.self_approve_with_justification_threshold &&
           !needsRevalidation && !hasUnsavedChanges && (
            <div className="relative group">
              <button
                type="button"
                onClick={() => handleSave('submitted')}
                disabled={saving}
                className="btn btn-primary flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white"
              >
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                Send til studieforbund
              </button>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <div className="flex items-center gap-1">
                  <Info size={12} />
                  Score {validationResult.score.toFixed(0)} — krever grundig begrunnelse
                </div>
              </div>
            </div>
          )}

          {/* MEMBER — sone 4: rød, avvist, må forbedres — INGEN send-knapp */}
          {currentUser?.role === 'member' && validationRunThisSession && validationResult &&
           validationResult.score < thresholds.manual_review_threshold &&
           !needsRevalidation && !hasUnsavedChanges && (
            <div className="basis-full">
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
                <AlertTriangle className="text-red-600 flex-shrink-0" size={22} />
                <div className="text-sm">
                  <p className="font-semibold text-red-900 mb-1">
                    Planen må forbedres før innsending
                  </p>
                  <p className="text-red-800">
                    Score {validationResult.score.toFixed(0)}% er under kvalitetskravet ({thresholds.manual_review_threshold}%).
                    Bruk KI-forslag til justeringer eller redigér manuelt, deretter kjør validering på nytt.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ADMIN/SYSTEMADMIN — sone 1: grønn, enkel godkjenning */}
          {(id || createdPlanId) && validationResult &&
           validationResult.score >= thresholds.auto_approve_threshold &&
           currentUser && (currentUser.role === 'admin' || currentUser.role === 'systemadmin') &&
           !needsRevalidation && !hasUnsavedChanges && (
            <button
              type="button"
              onClick={() => setShowApproveModal(true)}
              disabled={approving || saving}
              className="btn btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              {approving ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
              Godkjenn
            </button>
          )}

          {/* ADMIN/SYSTEMADMIN — sone 2/3: gul, godkjenn med begrunnelse */}
          {(id || createdPlanId) && validationResult &&
           validationResult.score >= thresholds.manual_review_threshold &&
           validationResult.score < thresholds.auto_approve_threshold &&
           currentUser && (currentUser.role === 'admin' || currentUser.role === 'systemadmin') &&
           !needsRevalidation && !hasUnsavedChanges && (
            <button
              type="button"
              onClick={() => setShowApproveModal(true)}
              disabled={approving || saving}
              className="btn flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white"
            >
              {approving ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
              Godkjenn med begrunnelse
            </button>
          )}

          {/* ADMIN/SYSTEMADMIN — sone 4: rød, overprøv KI (unntaks-godkjenning) */}
          {(id || createdPlanId) && validationResult &&
           validationResult.score < thresholds.manual_review_threshold &&
           currentUser && (currentUser.role === 'admin' || currentUser.role === 'systemadmin') &&
           !needsRevalidation && !hasUnsavedChanges && (
            <button
              type="button"
              onClick={() => setShowOverrideModal(true)}
              disabled={approving || saving}
              className="btn flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
            >
              {approving ? <Loader2 className="animate-spin" size={18} /> : <AlertTriangle size={18} />}
              Overprøv KI og godkjenn
            </button>
          )}

          <button
            type="button"
            onClick={handleValidateClick}
            disabled={validating || !(id || createdPlanId)}
            title={!(id || createdPlanId) ? 'Lagre utkast først for å kjøre KI-validering' : ''}
            className={`btn flex items-center gap-2 ${
              !(id || createdPlanId)
                ? 'btn-secondary opacity-50 cursor-not-allowed'
                : 'btn-primary bg-kf-accent hover:bg-kf-accent'
            }`}
          >
            {validating ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
            KI-validering
          </button>

          <button
            type="button"
            onClick={handleCancelClick}
            className="btn btn-secondary"
          >
            Avbryt
          </button>
        </div>
      </div>

      {/* Ulagrede endringer-modal (task #303) */}
      {showUnsavedChangesModal && (
        <UnsavedChangesModal
          isExistingPlan={!!id}
          onSaveAndLeave={handleSaveAndLeave}
          onDiscardAndLeave={handleDiscardAndLeave}
          onKeepEditing={handleKeepEditing}
        />
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Godkjenn studieplan</h3>

            {/* KI-validering resultat (ikke-redigerbar) */}
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded">
              <h4 className="font-semibold text-sm text-gray-900 mb-2">KI-VALIDERING</h4>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Score:</span>
                  <span className={`font-bold ${
                    (Number(validationResult?.score) || 0) >= 70 ? 'text-green-600' : 'text-kf-warning'
                  }`}>
                    {Number(validationResult?.score || 0).toFixed(1)}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Tilbakemelding:</span>
                  <p className="text-gray-800 mt-1">{validationResult?.feedback.summary}</p>
                </div>
              </div>
            </div>

            {/* Check if score is below threshold */}
            {(Number(validationResult?.score) || 0) < 70 ? (
              <>
                <p className="text-gray-600 mb-4">
                  Du må begrunne hvorfor du godkjenner denne studieplanen til tross for lav AI-score. Begrunnelsen vil bli lagret
                  sammen med KI-valideringsresultatet og sendt til brukeren.
                </p>

                <div className="mb-4">
                  <label className="label text-sm font-medium mb-2">
                    Begrunnelse for godkjenning (påkrevd) *
                  </label>
                  <textarea
                    className="input"
                    rows={4}
                    placeholder="Skriv inn hvorfor du godkjenner studieplanen til tross for KI-tilbakemeldingen..."
                    value={approveComment}
                    onChange={(e) => setApproveComment(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Eksempel: "Studieplanen oppfyller minimumskrav for voksenopplæring. Manglene som KI-en påpeker er mindre vesentlige og vil bli adressert i neste revisjon."
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-4">
                  Er du sikker på at du vil godkjenne denne studieplanen? Studieplanen oppfyller kvalitetskravene basert på KI-valideringen.
                </p>

                <div className="mb-4">
                  <label className="label text-sm font-medium mb-2">
                    Ytterligere merknader til godkjenningen:
                  </label>
                  <textarea
                    className="input"
                    rows={3}
                    placeholder="Valgfritt: Legg til merknader til godkjenningen..."
                    value={approveComment}
                    onChange={(e) => setApproveComment(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                disabled={approving || ((Number(validationResult?.score) || 0) < 70 && !approveComment.trim())}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {approving ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <CheckCircle size={18} />
                )}
                Godkjenn
              </button>
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setApproveComment('');
                }}
                disabled={approving}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Self-Approve Modal */}
      {showSelfApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Selvgodkjenn studieplan</h3>

            <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <p className="font-medium text-green-900">KI-validering bestått</p>
              </div>
              <p className="text-sm text-green-800">
                Studieplanen din har oppnådd en KI-score på <strong>{Number(validationResult?.score || 0).toFixed(1)}%</strong>,
                som er over terskelverdien. Du kan nå godkjenne planen selv uten å sende den til studieforbundet.
              </p>
            </div>

            <p className="text-gray-600 mb-4">
              Ved selvgodkjenning vil det genereres en unik verifiseringslenke som kan deles offentlig.
              Du kan også velge å sende planen til studieforbundet for manuell vurdering i stedet.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleSelfApprove}
                disabled={approving}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
              >
                {approving ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <CheckCircle size={18} />
                )}
                Godkjenn selv
              </button>
              <button
                onClick={() => setShowSelfApproveModal(false)}
                disabled={approving}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Override Modal - for admin/systemadmin when score < 70 */}
      {showOverrideModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <AlertTriangle className="text-amber-600" size={24} />
              Overprøv KI-validering
            </h3>

            {/* Warning box */}
            <div className="bg-amber-50 border border-amber-200 rounded p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-900 mb-1">Viktig informasjon</p>
                  <p className="text-sm text-amber-800">
                    Du er i ferd med å godkjenne en studieplan som <strong>ikke oppfyller kvalitetskravene</strong> basert på KI-valideringen.
                    KI-scoren er <strong className="text-red-700">{Number(validationResult?.score || 0).toFixed(1)}%</strong> (under 70%).
                  </p>
                </div>
              </div>
            </div>

            {/* Consequences */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
              <p className="font-medium text-gray-900 mb-2">Konsekvenser ved overprøving:</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>Begrunnelsen blir synlig for systemadministrator</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>Begrunnelsen blir synlig for eier-organisasjonen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>Begrunnelsen blir <strong>ikke</strong> vist på offentlig verifiseringsside</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>Studieplanen vil likevel få godkjenningslenke og QR-kode</span>
                </li>
              </ul>
            </div>

            {/* KI Feedback summary */}
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <p className="font-medium text-red-900 mb-1">KI-tilbakemelding:</p>
              <p className="text-sm text-red-800">{validationResult?.feedback?.summary || 'Ingen tilbakemelding'}</p>
            </div>

            {/* Override reason input */}
            <div className="mb-4">
              <label className="label text-sm font-medium mb-2">
                Begrunnelse for overprøving (påkrevd) *
              </label>
              <textarea
                className="input"
                rows={4}
                placeholder="Skriv en detaljert begrunnelse for hvorfor du godkjenner denne studieplanen til tross for lav KI-score..."
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 30 tegn. Begrunnelsen skal forklare hvilke kvalitetssikringsmekanismer som er brukt i stedet for KI-valideringen.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleOverrideApprove}
                disabled={approving || overrideReason.trim().length < 30}
                className="btn flex-1 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white disabled:bg-gray-400"
              >
                {approving ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <AlertTriangle size={18} />
                )}
                Overprøv og godkjenn
              </button>
              <button
                onClick={() => {
                  setShowOverrideModal(false);
                  setOverrideReason('');
                }}
                disabled={approving}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit for Review Modal - for plans with rejected_with_revisions decision */}
      {showSubmitForReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <AlertTriangle className="text-yellow-600" size={24} />
              Send inn med begrunnelse
            </h3>

            {/* Warning box */}
            <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-yellow-900 mb-1">Studieplanen krever revisjon</p>
                  <p className="text-sm text-yellow-800">
                    KI-valideringen ga en score på <strong>{Number(validationResult?.score || 0).toFixed(1)}%</strong> (mellom 60-79%),
                    noe som betyr at planen trenger forbedringer før den kan selvgodkjennes.
                  </p>
                </div>
              </div>
            </div>

            {/* Info about submission */}
            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded p-4 mb-4">
              <p className="text-sm text-kf-blue-800">
                Du kan likevel sende studieplanen til studieforbundet for manuell vurdering.
                Du må oppgi en begrunnelse for hvorfor planen bør vurderes til tross for KI-tilbakemeldingen.
              </p>
            </div>

            {/* KI Feedback summary */}
            {validationResult?.feedback?.improvement_suggestions && validationResult.feedback.improvement_suggestions.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                <p className="font-medium text-gray-900 mb-2">KI-forslag til forbedring:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  {validationResult.feedback.improvement_suggestions.slice(0, 3).map((suggestion: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Justification input */}
            <div className="mb-4">
              <label className="label text-sm font-medium mb-2">
                Begrunnelse for innsending (påkrevd) *
              </label>
              <textarea
                className="input"
                rows={4}
                placeholder="Forklar hvorfor studieplanen bør vurderes av studieforbundet til tross for at den ikke oppfyller alle KI-kravene..."
                value={submissionJustification}
                onChange={(e) => setSubmissionJustification(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 50 tegn. Begrunnelsen sendes til studieforbundet sammen med studieplanen.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {submissionJustification.length}/50 tegn
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSubmitForReview}
                disabled={submittingForReview || submissionJustification.trim().length < 50}
                className="btn flex-1 flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white disabled:bg-gray-400"
              >
                {submittingForReview ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <AlertTriangle size={18} />
                )}
                Send til vurdering
              </button>
              <button
                onClick={() => {
                  setShowSubmitForReviewModal(false);
                  setSubmissionJustification('');
                }}
                disabled={submittingForReview}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
